import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import DOMPurify from 'dompurify';
import { SketchPicker } from 'react-color';
import Login from './Login';
import Signup from './Signup';
import { supabase, sendMail, getMailsForUser, getFoldersForUser, getDeletedFoldersForUser, createFolder, moveMailToFolder as moveMailToFolderSupabase, moveMailToFolderByName, saveDraft, updateDraft, uploadDrawing, removeMailFromFolder, updateDraftToSent, deleteMailFromSupabase, softDeleteFolder, restoreFolderSupabase, deleteFolderFromSupabase, updateUserLocation, updateTutorialStatus } from './supabase';

const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const getFolderIcon = (name) => {
  switch (name.toLowerCase()) {
    case 'inbox': return '📥';
    case 'sent': return '📤';
    case 'drafts': return '📝';
    case 'bin': return '🗑️';
    case 'hidden': return '🔒';
    default: return '📁';
  }
};

const DrawingCanvas = ({ width, height, placeholder, value, onChange, brushColor, brushSize, isEraser, backgroundColor, brushType, onDrawingStateChange }) => {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const prevHeightRef = useRef(height);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');

    // If height changed, we need to preserve existing content without stretching
    if (prevHeightRef.current !== height && historyIndex >= 0) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

      canvas.width = width * 2;
      canvas.height = height * 2;
      canvas.style.width = '100%';
      canvas.style.height = `${height}px`;

      context.scale(2, 2);
      context.lineCap = 'round';
      context.lineJoin = 'round';

      // Draw back the old content
      context.drawImage(tempCanvas, 0, 0, width, prevHeightRef.current);
      prevHeightRef.current = height;
    } else {
      canvas.width = width * 2;
      canvas.height = height * 2;
      canvas.style.width = '100%';
      canvas.style.height = `${height}px`;

      context.scale(2, 2);
      context.lineCap = 'round';
      context.lineJoin = 'round';
    }

    contextRef.current = context;

    if (value && history.length === 0) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = value;
      image.onload = () => {
        context.drawImage(image, 0, 0, width, height);
        setHistory([canvas.toDataURL()]);
        setHistoryIndex(0);
      };
    } else if (history.length === 0) {
      context.clearRect(0, 0, width, height);
      setHistory([canvas.toDataURL()]);
      setHistoryIndex(0);
    }
  };

  useEffect(() => {
    initializeCanvas();
  }, [width, height]);

  const saveToHistory = (dataUrl) => {
    setHistory(prev => [...prev.slice(0, historyIndex + 1), dataUrl]);
    setHistoryIndex(prev => prev + 1);
  };

  const startDrawing = ({ nativeEvent }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width / 2;
    const scaleY = canvas.height / rect.height / 2;
    const x = (nativeEvent.clientX - rect.left) * scaleX;
    const y = (nativeEvent.clientY - rect.top) * scaleY;

    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
    setIsDrawing(true);
    if (onDrawingStateChange) onDrawingStateChange(true);
  };

  const finishDrawing = () => {
    if (!isDrawing) return;
    contextRef.current.closePath();
    setIsDrawing(false);
    if (onDrawingStateChange) onDrawingStateChange(false);
    const dataUrl = canvasRef.current.toDataURL();
    saveToHistory(dataUrl);
    onChange(dataUrl);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width / 2;
    const scaleY = canvas.height / rect.height / 2;
    const x = (nativeEvent.clientX - rect.left) * scaleX;
    const y = (nativeEvent.clientY - rect.top) * scaleY;

    const ctx = contextRef.current;

    if (isEraser) {
      ctx.strokeStyle = backgroundColor || '#ffffff';
      ctx.lineWidth = brushSize;
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
    } else {
      ctx.strokeStyle = brushColor;
      ctx.lineWidth = brushSize;

      if (brushType === 'calligraphy') {
        ctx.lineWidth = brushSize * 2;
        ctx.lineCap = 'square';
        // Simulate calligraphy by slightly shifting the line
        ctx.lineTo(x + 2, y + 2);
      } else if (brushType === 'soft') {
        ctx.shadowBlur = brushSize;
        ctx.shadowColor = brushColor;
        ctx.lineCap = 'round';
      } else {
        // Normal
        ctx.setLineDash([]);
        ctx.shadowBlur = 0;
        ctx.lineCap = 'round';
      }
    }

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const undo = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    const prevImage = new Image();
    prevImage.crossOrigin = 'anonymous';
    prevImage.src = history[newIndex];
    prevImage.onload = () => {
      contextRef.current.clearRect(0, 0, width, height);
      contextRef.current.drawImage(prevImage, 0, 0, width, height);
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    };
  };

  const clear = () => {
    contextRef.current.clearRect(0, 0, width, height);
    const dataUrl = canvasRef.current.toDataURL();
    saveToHistory(dataUrl);
    onChange(null);
  };

  return (
    <div className="drawing-container" style={{ position: 'relative', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', backgroundColor: backgroundColor || '#fff' }}>
      {placeholder && !value && !isDrawing && (
        <div className="canvas-placeholder" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#ccc', pointerEvents: 'none', fontStyle: 'italic', fontSize: '0.9em' }}>
          {placeholder}
        </div>
      )}
      <canvas
        onMouseDown={startDrawing}
        onMouseUp={finishDrawing}
        onMouseMove={draw}
        onMouseOut={() => isDrawing && finishDrawing()}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          const mouseEvent = new MouseEvent("mousedown", {
            clientX: touch.clientX,
            clientY: touch.clientY
          });
          startDrawing({ nativeEvent: mouseEvent });
        }}
        onTouchEnd={finishDrawing}
        onTouchMove={(e) => {
          const touch = e.touches[0];
          const mouseEvent = new MouseEvent("mousemove", {
            clientX: touch.clientX,
            clientY: touch.clientY
          });
          draw({ nativeEvent: mouseEvent });
        }}
        ref={canvasRef}
        style={{ display: 'block', width: '100%', cursor: isEraser ? 'cell' : 'crosshair', touchAction: 'none' }}
      />
      <div className="canvas-actions" style={{ position: 'absolute', bottom: '5px', right: '5px', display: 'flex', gap: '5px' }}>
        <button type="button" onClick={undo} disabled={historyIndex <= 0} style={{ padding: '2px 8px', fontSize: '10px', borderRadius: '4px', border: '1px solid #ddd', background: '#f8f9fa', cursor: 'pointer' }}>Undo</button>
        <button type="button" onClick={clear} style={{ padding: '2px 8px', fontSize: '10px', borderRadius: '4px', border: '1px solid #ddd', background: '#f8f9fa', cursor: 'pointer' }}>Clear</button>
      </div>
    </div>
  );
};

function App() {
  const [mails, setMails] = useState([]);
  const [selectedMail, setSelectedMail] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [composeForm, setComposeForm] = useState({
    to: '',
    subject: '',
    content: '',
    backgroundColor: '#ffffff',
    textColorVar: '#000000',
    bgColorVar: '#ffffff',
    isHandwritten: false
  });
  const contentRef = useRef(null);
  const mailBodyRef = useRef(null);
  const textColorInputRef = useRef(null);
  const textColorPickerRef = useRef(null);
  const textColorButtonRef = useRef(null);
  const highlightColorPickerRef = useRef(null);
  const highlightColorButtonRef = useRef(null);
  const wholeLetterBackgroundColorPickerRef = useRef(null);
  const wholeLetterBackgroundColorButtonRef = useRef(null);
  const isApplyingColorChange = useRef(false);
  const isApplyingHighlightChange = useRef(false);
  const isUserDrawing = useRef(false);

  const [folders, setFolders] = useState([]);
  const [deletedFolders, setDeletedFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showConfirmEmptyBin, setShowConfirmEmptyBin] = useState(false);
  const [showConfirmRestore, setShowConfirmRestore] = useState(false);
  const [folderToRestore, setFolderToRestore] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmRestoreMail, setShowConfirmRestoreMail] = useState(false);
  const [mailToRestore, setMailToRestore] = useState(null);
  const [selectedDeletedFolderId, setSelectedDeletedFolderId] = useState(null);
  const [mailOriginalFolders, setMailOriginalFolders] = useState({});
  const [showConfirmDeleteFolder, setShowConfirmDeleteFolder] = useState(false);
  const [folderToDeleteId, setFolderToDeleteId] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [showConfirmDeleteImage, setShowConfirmDeleteImage] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [isEmptyingBin, setIsEmptyingBin] = useState(false);
  const [isMailboxAnimating, setIsMailboxAnimating] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [buttonColor, setButtonColor] = useState('#000000');
  const [showHighlightColorPicker, setShowHighlightColorPicker] = useState(false);
  const [selectedHighlightColor, setSelectedHighlightColor] = useState('#ffffff');
  const [buttonHighlightColor, setButtonHighlightColor] = useState('#ffffff');
  const [showWholeLetterBackgroundColorPicker, setShowWholeLetterBackgroundColorPicker] = useState(false);
  const [selectedWholeLetterBackgroundColor, setSelectedWholeLetterBackgroundColor] = useState('#ffffff');
  const [buttonWholeLetterBackgroundColor, setButtonWholeLetterBackgroundColor] = useState('#ffffff');
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const folderDropdownRef = useRef(null);
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);
  const logoutDropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isReceivedExpanded, setIsReceivedExpanded] = useState(false);

  // Global error handler to silence persistent AbortError noise
  useEffect(() => {
    const handleError = (e) => {
      if (e.message?.includes('aborted') || e.reason?.name === 'AbortError') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  const filteredMails = useMemo(() => {
    if (selectedDeletedFolderId) {
      const deletedFolder = deletedFolders.find(f => f.id === selectedDeletedFolderId);
      if (!deletedFolder) return [];
      return mails.filter(mail => deletedFolder.mailIds.includes(mail.id)).filter(mail =>
        !searchTerm ||
        (mail.subject && mail.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (mail.sender && mail.sender.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (mail.content && stripHtml(mail.content).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } else {
      const folder = folders.find(f => f.id === selectedFolderId);
      if (!folder) return [];
      return folder.mailIds.map(id => mails.find(m => m.id === id)).filter(mail => mail)
        .filter(mail => !mail.hiddenUntil || new Date(mail.hiddenUntil) <= new Date())
        .filter(mail =>
          !searchTerm ||
          (mail.subject && mail.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (mail.sender && mail.sender.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (mail.content && stripHtml(mail.content).toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }
  }, [selectedDeletedFolderId, selectedFolderId, mails, folders, deletedFolders, searchTerm]);

  const globalSearchResults = useMemo(() => {
    if (!searchTerm || searchTerm.trim().length < 2) return [];

    const searchLower = searchTerm.toLowerCase();

    return mails.map(mail => {
      let score = 0;
      const subject = (mail.subject || '').toLowerCase();
      const content = stripHtml(mail.content || '').toLowerCase();
      const sender = (mail.sender || '').toLowerCase();
      const recipient = (mail.recipient || '').toLowerCase();

      // Subject matches
      if (subject === searchLower) score += 100;
      else if (subject.includes(searchLower)) score += 50;

      // Content matches
      if (content.includes(searchLower)) score += 20;

      // Sender/Recipient matches
      if (sender.includes(searchLower) || recipient.includes(searchLower)) score += 10;

      if (score === 0) return null;

      // Find folder location
      let folderName = 'Unknown';
      const folder = folders.find(f => f.mailIds.includes(mail.id));
      if (folder) {
        folderName = folder.name;
      } else {
        const deletedFolder = deletedFolders.find(f => f.mailIds.includes(mail.id));
        if (deletedFolder) {
          folderName = `Bin (${deletedFolder.name})`;
        } else if (mail.type === 'draft') {
          folderName = 'Drafts';
        }
      }

      return { ...mail, score, folderName };
    })
      .filter(mail => mail !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8); // Limit to top 8 results
  }, [mails, folders, deletedFolders, searchTerm]);

  const [isSendingDrafts, setIsSendingDrafts] = useState(false);
  const [sendingStatus, setSendingStatus] = useState(null);
  const [stampQueue, setStampQueue] = useState([]);
  const [currentStampDraft, setCurrentStampDraft] = useState(null);
  const [hoveredStamp, setHoveredStamp] = useState(null);
  const [showStampSelector, setShowStampSelector] = useState(false);
  const [composeMode, setComposeMode] = useState('type'); // 'type' or 'draw'
  const [brushSize, setBrushSize] = useState(2);
  const [brushType, setBrushType] = useState('normal'); // 'normal', 'calligraphy', 'soft'
  const [drawingHeight, setDrawingHeight] = useState(400);
  const [isEraser, setIsEraser] = useState(false);

  // Data is now stored in Supabase, no localStorage helpers needed

  // State for current formatting in toolbar
  const [editorFormatting, setEditorFormatting] = useState({
    font: 'Ibarra Real Nova',
    fontSize: '3',
    lineHeight: '',
    bold: false,
    italic: false,
    underline: false,
    alignLeft: false,
    alignCenter: false,
    alignRight: false,
    alignJustify: false,
    textColor: '#000000',
    highlightColor: '#ffffff',
    wholeLetterBackgroundColor: '#ffffff'
  });
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingImage, setResizingImage] = useState(null);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [originalDimensions, setOriginalDimensions] = useState(null);
  const [initialMouseX, setInitialMouseX] = useState(null);
  const [initialMouseY, setInitialMouseY] = useState(null);
  const [initialWrapperLeft, setInitialWrapperLeft] = useState(null);
  const [initialWrapperTop, setInitialWrapperTop] = useState(null);
  const [selectedImageWrapper, setSelectedImageWrapper] = useState(null);
  const [imageZIndexCounter, setImageZIndexCounter] = useState(1000);
  const [historyState, setHistoryState] = useState({ items: [], index: -1 });
  const saveHistoryTimeoutRef = useRef(null);
  const bufferedContentRef = useRef('');
  const syncTimeoutRef = useRef(null);
  const getFormattingTimeoutRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);

  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  // Handle authentication state and session recovery
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('Session found:', session.user.email);
          const user = session.user;
          const mappedUser = {
            username: user.user_metadata.username || user.email,
            email: user.email,
            full_name: user.user_metadata.full_name || '',
            id: user.id
          };
          setCurrentUser(mappedUser);
          setIsLoggedIn(true);
          localStorage.setItem('currentUser', JSON.stringify(mappedUser));
        } else {
          console.log('No active session found.');
        }
      } catch (err) {
        if (err.name !== 'AbortError' && !err.message?.includes('aborted')) {
          console.error('Error in checkSession:', err);
        }
      } finally {
        setIsInitializing(false);
        setTimeout(() => setShowLogin(true), 100);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const user = session.user;
        const mappedUser = {
          username: user.user_metadata.username || user.email,
          email: user.email,
          full_name: user.user_metadata.full_name || '',
          id: user.id
        };
        setCurrentUser(mappedUser);
        setIsLoggedIn(true);
        localStorage.setItem('currentUser', JSON.stringify(mappedUser));
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        setIsLoggedIn(false);
        localStorage.removeItem('currentUser');
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Show tutorial for new users
  useEffect(() => {
    if (isLoggedIn && currentUser && !isLoadingData) {
      if (currentUser.has_seen_tutorial === false) {
        // Reduced delay to ensure it shows up after initial data load
        const timer = setTimeout(() => setShowTutorial(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoggedIn, currentUser?.has_seen_tutorial, isLoadingData]);

  // Sync user data from database after login
  useEffect(() => {
    if (!currentUser || !currentUser.id) return;

    const syncUserData = async () => {
      try {
        // Try to find user by email first (more reliable for manual inserts)
        let { data: dbUser, error: dbUserError } = await supabase
          .from('users')
          .select('username, email, full_name, id, has_seen_tutorial')
          .eq('email', currentUser.email)
          .single();

        if (dbUserError) {
          console.log('User not found by email, trying by ID');
          const { data: dbUserById } = await supabase
            .from('users')
            .select('username, email, full_name, id, has_seen_tutorial')
            .eq('id', currentUser.id)
            .single();
          if (dbUserById) {
            dbUser = dbUserById;
          }
        }

        if (dbUser && (dbUser.username !== currentUser.username || dbUser.full_name !== currentUser.full_name || dbUser.has_seen_tutorial !== currentUser.has_seen_tutorial)) {
          console.log('Updating user from database:', dbUser);
          const updatedUser = {
            username: dbUser.username,
            email: dbUser.email,
            full_name: dbUser.full_name,
            id: dbUser.id,
            has_seen_tutorial: dbUser.has_seen_tutorial
          };
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        }
      } catch (err) {
        console.log('Background sync skipped:', err.message);
      }
    };

    // Delay sync slightly to avoid race with data loading
    const timeout = setTimeout(syncUserData, 500);
    return () => clearTimeout(timeout);
  }, [currentUser?.id]);

  // Initialize app - allow fonts to load (already handled in auth useEffect above)

  useEffect(() => {
    console.log('Data loading effect triggered. currentUser:', currentUser);
    if (currentUser && currentUser.id) {
      console.log('Current user exists, clearing loading state if data found.');
      if (mails.length === 0 && folders.length === 0) {
        setIsLoadingData(true);
      }
      const loadData = async () => {
        try {
          console.log('Loading data for user ID:', currentUser.id);

          // Check if user exists in database by ID
          const { data: userCheck, error: userCheckError } = await supabase
            .from('users')
            .select('id, username, email, has_seen_tutorial')
            .eq('id', currentUser.id)
            .single();

          if (userCheckError && userCheckError.code === 'PGRST116') {
            // User doesn't exist, create it
            console.log('User not found in database, creating...');
            const { data: authUser } = await supabase.auth.getUser();
            if (authUser.user) {
              await supabase.from('users').insert({
                id: authUser.user.id,
                username: currentUser.username,
                email: currentUser.email,
                full_name: currentUser.full_name,
                verified: true,
                password: 'auth_managed'
              });
            }
          }

          // Fetch data using user ID directly
          const { data: mailsData } = await supabase
            .from('mails')
            .select(`
              *,
              sender:users!sender_id(username),
              recipient:users!recipient_id(username)
            `)
            .or(`sender_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`)
            .order('sent_at', { ascending: false });

          const { data: foldersData } = await supabase
            .from('folders')
            .select(`
              *,
              folder_mails(mail_id)
            `)
            .eq('user_id', currentUser.id)
            .eq('deleted', false);

          const { data: deletedFoldersData } = await supabase
            .from('folders')
            .select(`
              *,
              folder_mails(mail_id)
            `)
            .eq('user_id', currentUser.id)
            .eq('deleted', true);

          console.log('Loaded folders:', foldersData);
          console.log('Loaded mails:', mailsData);

          // Update user location silently
          updateUserLocation(currentUser.id);

          // Transform data
          const transformedMails = (mailsData || []).map(mail => ({
            id: mail.id,
            sender: mail.sender?.username || '',
            recipient: mail.recipient?.username || '',
            subject: mail.subject,
            content: mail.content,
            sentAt: mail.sent_at,
            country: mail.country,
            type: mail.type || (mail.sender_id === currentUser.id ? 'sent' : 'received'),
            opened: mail.opened,
            backgroundColor: mail.background_color,
            textColorVar: mail.text_color_var,
            bgColorVar: mail.bg_color_var,
            hiddenUntil: mail.hidden_until,
            isHandwritten: mail.is_handwritten || false,
            stamp: mail.stamp
          }));

          const transformedFolders = (foldersData || []).map(folder => ({
            id: folder.id,
            name: folder.name,
            mailIds: folder.folder_mails ? folder.folder_mails.map(fm => fm.mail_id) : []
          }));

          const transformedDeletedFolders = (deletedFoldersData || []).map(folder => ({
            id: folder.id,
            name: folder.name,
            mailIds: folder.folder_mails ? folder.folder_mails.map(fm => fm.mail_id) : []
          }));

          // Create missing default folders
          let finalFolders = [...transformedFolders];
          const defaultFolders = ['Inbox', 'Hidden', 'Bin', 'Drafts'];
          for (const folderName of defaultFolders) {
            if (!finalFolders.find(f => f.name === folderName)) {
              try {
                const { data: newFolder } = await supabase
                  .from('folders')
                  .insert({
                    user_id: currentUser.id,
                    name: folderName
                  })
                  .select()
                  .single();

                if (newFolder) {
                  finalFolders.push({ id: newFolder.id, name: newFolder.name, mailIds: [] });
                }
              } catch (err) {
                console.error(`Failed to create ${folderName} folder:`, err);
              }
            }
          }

          setMails(transformedMails);
          setFolders(finalFolders);
          setDeletedFolders(transformedDeletedFolders);
          setMailOriginalFolders({});

          const inbox = finalFolders.find(f => f.name === 'Inbox');
          if (inbox) {
            setSelectedFolderId(inbox.id);
          }
        } catch (error) {
          console.error('Error loading data:', error);
        } finally {
          setIsLoadingData(false);
        }
      };
      loadData();
    }
  }, [currentUser?.id]);

  const selectFolderByName = (name) => {
    const folder = folders.find(f => f.name.toLowerCase() === name.toLowerCase());
    if (folder) {
      selectFolder(folder);
      return true;
    }
    return false;
  };

  const repairData = async () => {
    if (!currentUser) return;
    setIsLoadingData(true);
    try {
      const cleanUsername = currentUser.username.split(':')[0];
      const { data: authUser } = await supabase.auth.getUser();
      if (authUser.user) {
        await supabase.from('users').upsert({
          id: authUser.user.id,
          username: cleanUsername,
          email: currentUser.email,
          full_name: currentUser.full_name,
          verified: true,
          password: 'auth_managed'
        });
      }

      let foldersData = await getFoldersForUser(currentUser.username) || [];
      const defaultFolders = ['Inbox', 'Hidden', 'Bin'];
      for (const folderName of defaultFolders) {
        if (!foldersData.find(f => f.name === folderName)) {
          const newFolder = await createFolder(currentUser.username, folderName);
          if (newFolder) foldersData.push({ ...newFolder, mailIds: [] });
        }
      }
      setFolders(foldersData);
      alert('Data repair complete!');
    } catch (error) {
      alert('Repair failed: ' + error.message);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Trigger animation on page load if hidden mails > 0
  useEffect(() => {
    const hiddenFolder = folders.find(f => f.name === 'Hidden');
    const hiddenMailCount = hiddenFolder ? hiddenFolder.mailIds.length : 0;
    if (hiddenMailCount > 0) {
      setIsMailboxAnimating(true);
      setTimeout(() => setIsMailboxAnimating(false), 500);
    }
  }, [folders]);

  // Authentication handlers
  const handleLogin = () => {
    // Handled by onAuthStateChange listener
  };

  const handleSignup = () => {
    // Handled by onAuthStateChange listener or verification flow
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleSwitchToSignup = () => {
    setIsSignup(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignup(false);
  };

  // Helper functions for cursor position
  const getPath = (node, root) => {
    const path = [];
    while (node && node !== root) {
      let index = 0;
      let sibling = node.previousSibling;
      while (sibling) {
        index++;
        sibling = sibling.previousSibling;
      }
      path.unshift(index);
      node = node.parentNode;
    }
    if (node !== root) {
      // Node is not a descendant of root, return empty path
      return [];
    }
    return path;
  };

  const getNodeByPath = (root, path) => {
    let node = root;
    for (const index of path) {
      node = node.childNodes[index];
    }
    return node;
  };

  // Save and restore selection
  const saveSelection = (root) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      return {
        startPath: getPath(range.startContainer, root),
        startOffset: range.startOffset,
        endPath: getPath(range.endContainer, root),
        endOffset: range.endOffset,
        collapsed: range.collapsed
      };
    }
    return null;
  };

  const restoreSelection = (root, savedSelection) => {
    if (savedSelection) {
      try {
        const startNode = getNodeByPath(root, savedSelection.startPath);
        const endNode = getNodeByPath(root, savedSelection.endPath);
        const range = document.createRange();
        range.setStart(startNode, savedSelection.startOffset);
        range.setEnd(endNode, savedSelection.endOffset);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e) {
        // If restoring fails, focus at the end
        const selection = window.getSelection();
        selection.removeAllRanges();
        const range = document.createRange();
        range.selectNodeContents(root);
        range.collapse(false);
        selection.addRange(range);
      }
    }
  };

  // Custom undo/redo functions
  const saveHistory = () => {
    if (contentRef.current) {
      const selection = window.getSelection();
      let selectionData = null;
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        try {
          selectionData = {
            startPath: getPath(range.startContainer, contentRef.current),
            startOffset: range.startOffset,
            endPath: getPath(range.endContainer, contentRef.current),
            endOffset: range.endOffset,
            collapsed: range.collapsed
          };
        } catch (e) {
          console.log('Failed to save selection path:', e);
        }
      }
      const state = {
        html: contentRef.current.innerHTML,
        backgroundColor: contentRef.current.style.backgroundColor,
        textColorVar: contentRef.current.style.getPropertyValue('--text-color'),
        bgColorVar: contentRef.current.style.getPropertyValue('--bg-color'),
        lineHeight: contentRef.current.style.lineHeight,
        selection: selectionData
      };

      setHistoryState(prev => {
        // Only save if it's different from the current head of history
        const currentHead = prev.items[prev.index];
        if (currentHead && currentHead.html === state.html &&
          currentHead.backgroundColor === state.backgroundColor &&
          currentHead.textColorVar === state.textColorVar &&
          currentHead.bgColorVar === state.bgColorVar &&
          currentHead.lineHeight === state.lineHeight) {
          return prev;
        }

        const newItems = prev.items.slice(0, prev.index + 1);
        newItems.push(state);
        if (newItems.length > 50) newItems.shift();

        return {
          items: newItems,
          index: Math.min(newItems.length - 1, 49)
        };
      });
    }
  };

  const debouncedSaveHistory = () => {
    if (saveHistoryTimeoutRef.current) {
      clearTimeout(saveHistoryTimeoutRef.current);
    }
    saveHistoryTimeoutRef.current = setTimeout(() => {
      saveHistory();
    }, 1000); // Reduced to 1s for better responsiveness
  };

  const undo = () => {
    setHistoryState(prev => {
      if (prev.index <= 0) return prev;

      const newIndex = prev.index - 1;
      const state = prev.items[newIndex];

      if (contentRef.current && state) {
        contentRef.current.innerHTML = state.html;
        contentRef.current.style.backgroundColor = state.backgroundColor;
        contentRef.current.style.setProperty('--text-color', state.textColorVar);
        contentRef.current.style.setProperty('--bg-color', state.bgColorVar);
        contentRef.current.style.lineHeight = state.lineHeight;
        updateContentEditorHeight();
        contentRef.current.focus();

        if (state.selection) {
          setTimeout(() => {
            try {
              const startNode = getNodeByPath(contentRef.current, state.selection.startPath);
              const endNode = getNodeByPath(contentRef.current, state.selection.endPath);
              const range = document.createRange();
              range.setStart(startNode, state.selection.startOffset);
              range.setEnd(endNode, state.selection.endOffset);
              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            } catch (e) {
              const selection = window.getSelection();
              selection.removeAllRanges();
              const range = document.createRange();
              range.selectNodeContents(contentRef.current);
              range.collapse(false);
              selection.addRange(range);
            }
          }, 0);
        }
      }
      return { ...prev, index: newIndex };
    });
  };

  const redo = () => {
    setHistoryState(prev => {
      if (prev.index >= prev.items.length - 1) return prev;

      const newIndex = prev.index + 1;
      const state = prev.items[newIndex];

      if (contentRef.current && state) {
        contentRef.current.innerHTML = state.html;
        contentRef.current.style.backgroundColor = state.backgroundColor;
        contentRef.current.style.setProperty('--text-color', state.textColorVar);
        contentRef.current.style.setProperty('--bg-color', state.bgColorVar);
        contentRef.current.style.lineHeight = state.lineHeight;
        updateContentEditorHeight();
        contentRef.current.focus();

        if (state.selection) {
          setTimeout(() => {
            try {
              const startNode = getNodeByPath(contentRef.current, state.selection.startPath);
              const endNode = getNodeByPath(contentRef.current, state.selection.endPath);
              const range = document.createRange();
              range.setStart(startNode, state.selection.startOffset);
              range.setEnd(endNode, state.selection.endOffset);
              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            } catch (e) {
              const selection = window.getSelection();
              selection.removeAllRanges();
              const range = document.createRange();
              range.selectNodeContents(contentRef.current);
              range.collapse(false);
              selection.addRange(range);
            }
          }, 0);
        }
        setTimeout(getSelectedTextFormatting, 0);
      }
      return { ...prev, index: newIndex };
    });
  };

  // Track if any popup is open to darken the background
  const isAnyPopupOpen = selectedMail || isComposing || showConfirmEmptyBin || showConfirmRestore || showConfirmDelete || showConfirmRestoreMail || showConfirmDeleteFolder || showConfirmDeleteImage || expandedImage;

  useEffect(() => {
    if (isAnyPopupOpen) {
      document.body.classList.add('popup-open');
    } else {
      document.body.classList.remove('popup-open');
    }
  }, [isAnyPopupOpen]);

  // Update selected image wrapper class
  useEffect(() => {
    const imageWrappers = document.querySelectorAll('.image-wrapper');
    imageWrappers.forEach(wrapper => {
      if (wrapper === selectedImageWrapper) {
        wrapper.classList.add('selected');
      } else {
        wrapper.classList.remove('selected');
      }
    });
  }, [selectedImageWrapper]);

  // Set global function for image click
  useEffect(() => {
    window.handleImageClick = handleImageClick;
    return () => {
      delete window.handleImageClick;
    };
  }, []);

  // Attach click handlers to images in mail body
  useEffect(() => {
    if (mailBodyRef.current) {
      const handleImageClickInMail = (e) => {
        if (e.target.tagName === 'IMG') {
          handleImageClick(e.target.src);
        }
      };
      mailBodyRef.current.addEventListener('click', handleImageClickInMail);
      return () => {
        if (mailBodyRef.current) {
          mailBodyRef.current.removeEventListener('click', handleImageClickInMail);
        }
      };
    }
  }, [selectedMail]);

  // Update mail body height to accommodate images
  useEffect(() => {
    if (mailBodyRef.current && selectedMail) {
      const mailBody = mailBodyRef.current;
      const images = mailBody.querySelectorAll('.resizable-image');
      let maxBottom = 0;

      images.forEach(img => {
        const wrapper = img.parentElement;
        const top = parseFloat(wrapper.style.top) || 0;
        const height = parseFloat(wrapper.style.height) || img.offsetHeight;
        const bottom = top + height;
        maxBottom = Math.max(maxBottom, bottom);
      });

      // Set minimum height and add padding, but leave space for buttons (60px)
      const minHeight = 200;
      const buttonSpace = 60;
      const newHeight = Math.max(minHeight, maxBottom + 20 + buttonSpace);
      mailBody.style.minHeight = `${newHeight}px`;
    }
  }, [selectedMail]);



  useEffect(() => {
    const handleSelectionChange = () => {
      if (isComposing && contentRef.current && document.activeElement === contentRef.current) {
        getSelectedTextFormatting();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isComposing]);

  // Handle backspace key for deleting selected image
  useEffect(() => {
    if (contentRef.current) {
      const handleKeyDown = (e) => {
        if (isComposing && selectedImageWrapper && e.key === 'Backspace') {
          e.preventDefault();
          setImageToDelete(selectedImageWrapper);
          setShowConfirmDeleteImage(true);
        }
      };
      contentRef.current.addEventListener('keydown', handleKeyDown);
      return () => {
        if (contentRef.current) {
          contentRef.current.removeEventListener('keydown', handleKeyDown);
        }
      };
    }
  }, [isComposing, selectedImageWrapper]);

  // Close text color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (textColorPickerRef.current && !textColorPickerRef.current.contains(event.target)) {
        setShowTextColorPicker(false);
      }
    };

    if (showTextColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTextColorPicker]);

  // Close highlight color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (highlightColorPickerRef.current && !highlightColorPickerRef.current.contains(event.target)) {
        setShowHighlightColorPicker(false);
      }
    };

    if (showHighlightColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHighlightColorPicker]);

  // Close whole letter background color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wholeLetterBackgroundColorPickerRef.current && !wholeLetterBackgroundColorPickerRef.current.contains(event.target)) {
        setShowWholeLetterBackgroundColorPicker(false);
      }
    };

    if (showWholeLetterBackgroundColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showWholeLetterBackgroundColorPicker]);

  // Sync selectedColor and buttonColor with currentTextColor when picker is closed
  useEffect(() => {
    if (!showTextColorPicker && !isApplyingColorChange.current) {
      if (selectedColor !== editorFormatting.textColor) {
        // Defer the heavy color application to reduce lag on click outside
        setTimeout(() => {
          if (isApplyingColorChange.current) return; // Prevent if already applying
          isApplyingColorChange.current = true;
          // Apply the color change logic directly without calling handleTextColorChange to avoid loop
          if (contentRef.current) {
            contentRef.current.focus();
            const selection = window.getSelection();
            if (selection.rangeCount > 0 && !selection.isCollapsed) {
              // Apply to selected text
              document.execCommand('foreColor', false, selectedColor);
            } else {
              // Apply color to future text using execCommand with a temporary selection
              const tempNode = document.createTextNode('\u200B'); // zero-width space
              const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();

              if (selection.rangeCount > 0) {
                range.insertNode(tempNode);
              } else {
                contentRef.current.appendChild(tempNode);
                range.selectNode(tempNode);
              }

              const selectionObj = window.getSelection();
              selectionObj.removeAllRanges();
              selectionObj.addRange(range);
              document.execCommand('foreColor', false, selectedColor);

              // We keep the zero-width space as a placeholder for the color
              // It's invisible but holds the formatting
            }
          }
          setEditorFormatting(prev => ({ ...prev, textColor: selectedColor }));
          setSelectedColor(selectedColor);
          setButtonColor(selectedColor);
          isApplyingColorChange.current = false;
        }, 0);
      } else {
        setSelectedColor(editorFormatting.textColor);
        setButtonColor(editorFormatting.textColor);
      }
    }
  }, [showTextColorPicker, editorFormatting.textColor, selectedColor]);

  // Sync selectedHighlightColor and buttonHighlightColor with currentHighlightColor when picker is closed
  useEffect(() => {
    if (!showHighlightColorPicker && !isApplyingHighlightChange.current) {
      if (selectedHighlightColor !== editorFormatting.highlightColor) {
        // Defer the heavy color application to reduce lag on click outside
        setTimeout(() => {
          if (isApplyingHighlightChange.current) return; // Prevent if already applying
          isApplyingHighlightChange.current = true;
          // Apply the color change logic directly without calling handleHighlightColorChange to avoid loop
          if (contentRef.current) {
            contentRef.current.focus();
            const selection = window.getSelection();
            if (selection.rangeCount > 0 && !selection.isCollapsed) {
              // Apply to selected text
              document.execCommand('backColor', false, selectedHighlightColor);
            } else {
              // Apply color to future text using execCommand with a temporary selection
              const tempNode = document.createTextNode('\u200B'); // zero-width space
              const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();

              if (selection.rangeCount > 0) {
                range.insertNode(tempNode);
              } else {
                contentRef.current.appendChild(tempNode);
                range.selectNode(tempNode);
              }

              const selectionObj = window.getSelection();
              selectionObj.removeAllRanges();
              selectionObj.addRange(range);
              document.execCommand('backColor', false, selectedHighlightColor);
            }
          }
          setEditorFormatting(prev => ({ ...prev, highlightColor: selectedHighlightColor }));
          setSelectedHighlightColor(selectedHighlightColor);
          setButtonHighlightColor(selectedHighlightColor);
          isApplyingHighlightChange.current = false;
        }, 0);
      } else {
        setSelectedHighlightColor(editorFormatting.highlightColor);
        setButtonHighlightColor(editorFormatting.highlightColor);
      }
    }
  }, [showHighlightColorPicker, editorFormatting.highlightColor, selectedHighlightColor]);

  // Sync selectedWholeLetterBackgroundColor and buttonWholeLetterBackgroundColor with currentWholeLetterBackgroundColor when picker is closed
  useEffect(() => {
    if (!showWholeLetterBackgroundColorPicker) {
      if (selectedWholeLetterBackgroundColor !== editorFormatting.wholeLetterBackgroundColor) {
        // Defer the heavy color application to reduce lag on click outside
        setTimeout(() => {
          handleWholeLetterBackgroundColorChange(selectedWholeLetterBackgroundColor);
          setSelectedWholeLetterBackgroundColor(selectedWholeLetterBackgroundColor);
          setButtonWholeLetterBackgroundColor(selectedWholeLetterBackgroundColor);
          setEditorFormatting(prev => ({ ...prev, wholeLetterBackgroundColor: selectedWholeLetterBackgroundColor }));
        }, 0);
      } else {
        setSelectedWholeLetterBackgroundColor(editorFormatting.wholeLetterBackgroundColor);
        setButtonWholeLetterBackgroundColor(editorFormatting.wholeLetterBackgroundColor);
      }
    }
  }, [showWholeLetterBackgroundColorPicker, editorFormatting.wholeLetterBackgroundColor, selectedWholeLetterBackgroundColor]);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (folderDropdownRef.current && !folderDropdownRef.current.contains(event.target) && !event.target.closest('.folder-dropdown-btn')) {
        setShowFolderDropdown(false);
      }
    };

    if (showFolderDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFolderDropdown]);

  // Close logout dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (logoutDropdownRef.current && !logoutDropdownRef.current.contains(event.target) && !event.target.closest('.header-title-container')) {
        setShowLogoutDropdown(false);
      }
    };

    if (showLogoutDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogoutDropdown]);
  useEffect(() => {
    const handleMouseMove = (e) => {
      const editor = contentRef.current;
      if (isDragging && resizingImage && resizeDirection && originalDimensions && initialMouseX !== null && initialMouseY !== null && initialWrapperLeft !== null && initialWrapperTop !== null) {
        const deltaX = e.clientX - initialMouseX;
        const deltaY = e.clientY - initialMouseY;

        let newWidth = originalDimensions.width;
        let newHeight = originalDimensions.height;
        let newLeft = initialWrapperLeft;
        let newTop = initialWrapperTop;

        if (resizeDirection === 'right') {
          newWidth = Math.max(50, originalDimensions.width + deltaX);
        } else if (resizeDirection === 'left') {
          newWidth = Math.max(50, originalDimensions.width - deltaX);
          newLeft = initialWrapperLeft + deltaX;
        } else if (resizeDirection === 'bottom') {
          newHeight = Math.max(50, originalDimensions.height + deltaY);
          const contentEditor = resizingImage.closest('.content-editor');
          if (contentEditor) {
            const editorRect = contentEditor.getBoundingClientRect();
            const newBottom = initialWrapperTop + newHeight;
            const newMinHeight = newBottom; // Remove padding
            contentEditor.style.minHeight = `${newMinHeight}px`;
          }
        } else if (resizeDirection === 'top') {
          newHeight = Math.max(50, originalDimensions.height - deltaY);
          newTop = initialWrapperTop + deltaY;
        } else if (resizeDirection === 'top-left') {
          newWidth = Math.max(50, originalDimensions.width - deltaX);
          newHeight = Math.max(50, originalDimensions.height - deltaY);
          newLeft = initialWrapperLeft + deltaX;
          newTop = initialWrapperTop + deltaY;
        } else if (resizeDirection === 'top-right') {
          newWidth = Math.max(50, originalDimensions.width + deltaX);
          newHeight = Math.max(50, originalDimensions.height - deltaY);
          newTop = initialWrapperTop + deltaY;
        } else if (resizeDirection === 'bottom-left') {
          newWidth = Math.max(50, originalDimensions.width - deltaX);
          newHeight = Math.max(50, originalDimensions.height + deltaY);
          newLeft = initialWrapperLeft + deltaX;
        } else if (resizeDirection === 'bottom-right') {
          newWidth = Math.max(50, originalDimensions.width + deltaX);
          newHeight = Math.max(50, originalDimensions.height + deltaY);
        }

        // Constrain resizing within editor bounds (except bottom)
        if (editor) {
          const editorRect = editor.getBoundingClientRect();

          // Prevent going beyond left boundary
          if (newLeft < 0) {
            newLeft = 0;
            // Adjust width if necessary for left resize
            if (resizeDirection === 'left' || resizeDirection === 'top-left' || resizeDirection === 'bottom-left') {
              newWidth = initialWrapperLeft + originalDimensions.width;
            }
          }

          // Prevent going beyond top boundary
          if (newTop < 0) {
            newTop = 0;
            // Adjust height if necessary for top resize
            if (resizeDirection === 'top' || resizeDirection === 'top-left' || resizeDirection === 'top-right') {
              newHeight = initialWrapperTop + originalDimensions.height;
            }
          }

          // Prevent going beyond right boundary
          const maxRight = editorRect.width;
          if (newLeft + newWidth > maxRight) {
            if (resizeDirection === 'right' || resizeDirection === 'top-right' || resizeDirection === 'bottom-right') {
              newWidth = maxRight - newLeft;
            } else {
              newLeft = maxRight - newWidth;
            }
          }
        }

        resizingImage.style.width = `${newWidth}px`;
        resizingImage.style.height = `${newHeight}px`;

        const DESIGN_CONTENT_WIDTH = 610; // 650 - 25 - 15
        const currentContentWidth = (editor ? editor.clientWidth : 650) - 40;

        // innerX is the x coordinate relative to the text start (25px padding)
        const innerX = newLeft - 25;
        const P = (innerX / currentContentWidth) * 100;
        const normalizedInnerX = (innerX / currentContentWidth) * DESIGN_CONTENT_WIDTH;

        // The formula to keep Pos constant relative to content:
        // left = P% of container + (25 - 0.4*P)px
        const offset = 25 - (P * 0.4);
        const pxLimit = 25 + normalizedInnerX;

        const wrapper = resizingImage.parentElement;
        wrapper.style.left = `min(${pxLimit.toFixed(2)}px, calc(${P.toFixed(2)}% + ${offset.toFixed(2)}px))`;
        wrapper.style.top = `${newTop}px`;
        wrapper.style.width = 'fit-content';
        wrapper.style.maxWidth = 'calc(100% - 25px)';

        updateContentEditorHeight();
      } else if (isDragging && dragOffset.x !== 0 && dragOffset.y !== 0) {
        if (editor) {
          const editorRect = editor.getBoundingClientRect();
          let x = e.clientX - editorRect.left - dragOffset.x;
          let y = e.clientY - editorRect.top - dragOffset.y;

          const draggedElement = document.querySelector('.dragging');
          if (draggedElement) {
            const elementRect = draggedElement.getBoundingClientRect();
            const minX = 10;
            const maxX = editorRect.width - elementRect.width - 15;

            x = Math.max(minX, Math.min(x, maxX));
            y = Math.max(0, y);

            const elementBottom = y + elementRect.height;
            if (elementBottom > editorRect.height) {
              const newMinHeight = elementBottom;
              editor.style.minHeight = `${newMinHeight}px`;
            }

            const DESIGN_CONTENT_WIDTH = 610;
            const currentContentWidth = editor.clientWidth - 40;
            const innerX = x - 25;
            const P = (innerX / currentContentWidth) * 100;
            const normalizedInnerX = (innerX / currentContentWidth) * DESIGN_CONTENT_WIDTH;

            const offset = 25 - (P * 0.4);
            const pxLimit = 25 + normalizedInnerX;

            draggedElement.style.position = 'absolute';
            draggedElement.style.left = `min(${pxLimit.toFixed(2)}px, calc(${P.toFixed(2)}% + ${offset.toFixed(2)}px))`;
            draggedElement.style.top = `${y}px`;
            draggedElement.style.width = 'fit-content';
            draggedElement.style.maxWidth = 'calc(100% - 25px)';
          }
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setResizingImage(null);
      setResizeDirection(null);
      setOriginalDimensions(null);
      setInitialMouseX(null);
      setInitialMouseY(null);
      setInitialWrapperLeft(null);
      setInitialWrapperTop(null);
      setDragOffset({ x: 0, y: 0 });
      const draggedElement = document.querySelector('.dragging');
      if (draggedElement) {
        draggedElement.classList.remove('dragging');
      }
      saveHistory();
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, resizingImage, dragOffset, resizeDirection, originalDimensions, initialMouseX, initialMouseY, initialWrapperLeft, initialWrapperTop]);

  const openMail = (mail) => {
    const updatedMails = mails.map(m =>
      m.id === mail.id ? { ...m, opened: true } : m
    );
    setMails(updatedMails);
    setSelectedMail(mail);
  };

  // Clearfix-Based Layout for Mail Detail
  useEffect(() => {
    if (selectedMail) {
      const updateLayout = () => {
        const mailBody = document.querySelector('.mail-body');
        if (!mailBody) return;

        const wrappers = mailBody.querySelectorAll('.image-wrapper');
        let maxBottom = 0;

        // Calculate bottom for container height
        wrappers.forEach((wrapper) => {
          const top = parseFloat(wrapper.style.top) || 0;
          const height = wrapper.offsetHeight || 200;
          const bottom = top + height;
          maxBottom = Math.max(maxBottom, bottom);
        });

        // Set container height with buffer for buttons
        const minHeight = 450;
        mailBody.style.minHeight = `${Math.max(minHeight, maxBottom + 150)}px`;
      };

      // Run on mount
      const timer = setTimeout(updateLayout, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedMail]);

  const closeMail = () => {
    setSelectedMail(null);
  };

  const startReply = (mail) => {
    setComposeForm({
      to: mail.sender,
      subject: `Re: ${mail.subject}`,
      content: '',
      backgroundColor: '#ffffff',
      textColorVar: '#34495e',
      bgColorVar: '#ffffff',
      isHandwritten: false
    });
    setIsComposing(true);
    setSelectedMail(null);
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.style.fontFamily = 'Lora';
        contentRef.current.style.setProperty('--editor-font', 'Lora');
        contentRef.current.style.setProperty('--editor-size', '16px');
        contentRef.current.focus();
        saveHistory();
      }
    }, 0);
  };

  const adjustImagePositionsForDisplay = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imageWrappers = doc.querySelectorAll('.image-wrapper');

    const getCoord = (val) => {
      if (!val) return 0;
      // Robust parsing for various formats: min(pxLimit, calc(P% + offset)) or calc(25px + min(px, %))
      if (val.includes('min(')) {
        const match = val.match(/min\(([\d.]+)px/);
        return match ? parseFloat(match[1]) : 0;
      }
      if (val.includes('calc(') && val.includes('px')) {
        // Fallback for simple calc(px + ...) or similar
        const match = val.match(/([\d.]+)px/);
        return match ? parseFloat(match[1]) : 0;
      }
      return parseFloat(val) || 0;
    };

    imageWrappers.forEach(wrapper => {
      const topAdjustment = 85;

      // Get the pixel limit (which is 25 + normalizedInnerX)
      const pxLimit = getCoord(wrapper.style.left);
      const editorTop = getCoord(wrapper.style.top);

      // Extract innerEditorLeft (normalizedInnerX)
      const innerEditorLeft = Math.max(0, pxLimit - 25);

      const DESIGN_CONTENT_WIDTH = 610;
      const P = (innerEditorLeft / DESIGN_CONTENT_WIDTH) * 100;
      const offset = 25 - (P * 0.4);

      // Re-apply the stable formula for display
      wrapper.style.left = `min(${pxLimit.toFixed(2)}px, calc(${P.toFixed(2)}% + ${offset.toFixed(2)}px))`;
      wrapper.style.top = `${editorTop + topAdjustment}px`;
      wrapper.style.position = 'absolute';
      wrapper.style.visibility = 'visible';

      const img = wrapper.querySelector('img');
      if (img) {
        img.setAttribute('onclick', 'window.handleImageClick(this.src)');
        img.style.cursor = 'pointer';
        wrapper.style.width = 'fit-content';
        wrapper.style.maxWidth = 'calc(100% - 25px)';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';
      }
    });

    return doc.body.innerHTML;
  };

  // Function to handle image click for expansion
  const handleImageClick = (src) => {
    setExpandedImage(src);
  };
  window.handleImageClick = handleImageClick;

  // Close expanded image
  const closeExpandedImage = () => {
    setExpandedImage(null);
  };

  const editDraft = (mail) => {
    setComposeForm({
      to: mail.recipient,
      subject: mail.subject,
      content: mail.content,
      backgroundColor: mail.backgroundColor || '#ffffff',
      textColorVar: mail.textColorVar || '#34495e',
      bgColorVar: mail.bgColorVar || '#ffffff',
      isHandwritten: mail.isHandwritten || false
    });
    setIsComposing(true);
    setSelectedMail(null);
    setEditingDraftId(mail.id);
    bufferedContentRef.current = mail.content;
    setComposeMode(mail.isHandwritten ? 'draw' : 'type');
    // Restore formatting after a short delay to ensure the editor is rendered
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.style.fontFamily = 'Lora';
        contentRef.current.style.setProperty('--editor-font', 'Lora');
        contentRef.current.style.setProperty('--editor-size', '16px');
        // Safely restore HTML content
        contentRef.current.innerHTML = DOMPurify.sanitize(mail.content);
        // Update editor height immediately after setting content
        updateContentEditorHeight();
        if (mail.backgroundColor) {
          contentRef.current.style.backgroundColor = mail.backgroundColor;
          contentRef.current.style.setProperty('--custom-bg', mail.backgroundColor);
          setSelectedWholeLetterBackgroundColor(mail.backgroundColor);
          setButtonWholeLetterBackgroundColor(mail.backgroundColor);
        }
        // Restore CSS variables for future text
        if (mail.textColorVar) {
          contentRef.current.style.setProperty('--text-color', mail.textColorVar);
          setSelectedColor(mail.textColorVar);
          setButtonColor(mail.textColorVar);
        }
        if (mail.bgColorVar) {
          contentRef.current.style.setProperty('--bg-color', mail.bgColorVar);
          setSelectedHighlightColor(mail.bgColorVar);
          setButtonHighlightColor(mail.bgColorVar);
        }
        // Update editor height again after a delay to ensure images are rendered
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.setStart(contentRef.current, 0);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        performFormattingCheck();
        saveHistory();
      }
    }, 100);
  };

  const startCompose = () => {
    setComposeForm({
      to: '',
      subject: '',
      content: '',
      backgroundColor: '#ffffff',
      textColorVar: '#000000',
      bgColorVar: '#ffffff',
      isHandwritten: false
    });
    setIsComposing(true);
    setSelectedMail(null);
    setEditingDraftId(null);
    setImageZIndexCounter(1000);
    bufferedContentRef.current = '';
    setComposeMode('type');
    setDrawingHeight(400);
    // Reset formatting state
    setEditorFormatting({
      font: 'Lora',
      fontSize: '3',
      lineHeight: '',
      bold: false,
      italic: false,
      underline: false,
      alignLeft: false,
      alignCenter: false,
      alignRight: false,
      alignJustify: false,
      textColor: '#000000',
      highlightColor: '#ffffff',
      wholeLetterBackgroundColor: '#ffffff'
    });
    // Ensure left alignment for new compose
    setTimeout(() => {
      if (contentRef.current) {
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('defaultParagraphSeparator', false, 'p');
        contentRef.current.setAttribute('dir', 'ltr');
        contentRef.current.style.textAlign = 'left';
        contentRef.current.style.lineHeight = '1.6';
        contentRef.current.style.fontFamily = 'Lora';
        contentRef.current.style.setProperty('--editor-font', 'Lora');
        contentRef.current.style.setProperty('--editor-size', '16px');
        contentRef.current.style.setProperty('--text-color', '#000000');
        contentRef.current.style.setProperty('--bg-color', '#ffffff');
        contentRef.current.style.setProperty('--custom-bg', 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)');
        contentRef.current.style.backgroundColor = '';
        contentRef.current.innerHTML = '';

        // Reset all picker states
        setSelectedColor('#000000');
        setButtonColor('#000000');
        setSelectedHighlightColor('#ffffff');
        setButtonHighlightColor('#ffffff');
        setSelectedWholeLetterBackgroundColor('#ffffff');
        setButtonWholeLetterBackgroundColor('#ffffff');


        setHistoryState({ items: [], index: -1 });
        saveHistory();
        contentRef.current.focus();
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.collapse(false); // Move cursor to end for LTR
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }, 0);
  };

  const handleModeToggle = (mode) => {
    setComposeMode(mode);
    setDrawingHeight(400);
  };

  const handleComposeChange = (e) => {
    const { name, value } = e.target;
    // Sanitize subject and recipient, but preserve content HTML (handled by editor)
    const cleanValue = (name === 'subject' || name === 'to')
      ? sanitizeInput(value)
      : value;

    setComposeForm(prev => ({
      ...prev,
      [name]: cleanValue
    }));
  };

  const [isSaving, setIsSaving] = useState(false);

  const performSaveDraft = async (silent = false) => {
    if (!currentUser) {
      if (!silent) alert('You must be logged in to save drafts.');
      return false;
    }

    if (isSaving) return false;

    let content = composeMode === 'draw' ? composeForm.content : (contentRef.current ? contentRef.current.innerHTML : '');
    let subject = composeForm.subject;
    const recipient = composeForm.to.trim();

    // Only save if there is a subject or content
    if (subject || content || recipient) {
      setIsSaving(true);
      try {
        // Handle drawing uploads if in draw mode and content is base64
        if (composeMode === 'draw') {
          if (subject && subject.startsWith('data:image/')) {
            subject = await uploadDrawing(currentUser.id, subject, 'subject');
            setComposeForm(prev => ({ ...prev, subject }));
          }
          if (content && content.startsWith('data:image/')) {
            content = await uploadDrawing(currentUser.id, content, 'content');
            setComposeForm(prev => ({ ...prev, content }));
          }
        }

        if (editingDraftId) {
          await updateDraft(
            editingDraftId,
            recipient,
            subject,
            content,
            composeForm.backgroundColor,
            composeForm.textColorVar,
            composeForm.bgColorVar,
            composeMode === 'draw'
          );
        } else {
          const newDraft = await saveDraft(
            currentUser.username,
            recipient,
            subject,
            content,
            composeForm.backgroundColor,
            composeForm.textColorVar,
            composeForm.bgColorVar,
            composeMode === 'draw'
          );
          setEditingDraftId(newDraft.id);
        }

        // Refresh data from Supabase
        const updatedMails = await getMailsForUser(currentUser.username);
        const updatedFolders = await getFoldersForUser(currentUser.username);
        setMails(updatedMails);
        setFolders(updatedFolders);

        // Switch to Drafts folder if not already there
        const draftsFolder = updatedFolders.find(f => f.name === 'Drafts');
        if (draftsFolder && selectedFolderId !== draftsFolder.id) {
          setSelectedFolderId(draftsFolder.id);
        }

        return true;
      } catch (error) {
        console.error('Error saving draft:', error);
        if (!silent) alert('Failed to save draft. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    }
    return true; // Nothing to save is considered a success/no-op
  };

  const saveAsDraft = async () => {
    const success = await performSaveDraft(false);
    if (success) {
      setComposeForm({
        to: '',
        subject: '',
        content: '',
        backgroundColor: '#ffffff',
        textColorVar: '#34495e',
        bgColorVar: '#ffffff',
        isHandwritten: false
      });
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
      }
      setIsComposing(false);
      setEditingDraftId(null);
      setComposeMode('type');
    }
  };

  const handleCloseCompose = async () => {
    // If user is currently drawing, don't close (prevent accidental closure while drawing strokes)
    if (isUserDrawing.current) return;

    // Attempt to save before closing
    const content = composeMode === 'draw' ? composeForm.content : (contentRef.current ? contentRef.current.innerHTML : '');
    if (composeForm.subject || content || composeForm.to) {
      const saved = await performSaveDraft(true);
      // If save was in progress or failed, we might want to wait or handle it,
      // but the requirement is to "save first then close".
    }

    // Then close
    cancelCompose();
  };

  const sendAllDrafts = async () => {
    if (!currentUser) {
      alert('You must be logged in to send drafts.');
      return;
    }

    const draftFolder = folders.find(f => f.name === 'Drafts');
    if (draftFolder && draftFolder.mailIds.length > 0) {
      const draftsToSend = draftFolder.mailIds
        .map(id => mails.find(m => m.id === id))
        .filter(mail => mail);

      if (draftsToSend.length > 0) {
        setStampQueue(draftsToSend);
        setCurrentStampDraft(draftsToSend[0]);
        setShowStampSelector(true);
      } else {
        alert('No drafts to send.');
      }
    } else {
      alert('No drafts to send.');
    }
  };

  const handleSendSingleDraft = async (mailId) => {
    if (!currentUser) {
      alert('You must be logged in to send drafts.');
      return;
    }

    const mail = mails.find(m => m.id === mailId);
    if (!mail) {
      alert('Cannot send draft: Draft not found.');
      return;
    }

    setStampQueue([mail]);
    setCurrentStampDraft(mail);
    setShowStampSelector(true);
  };

  const handleStampChoice = async (stampChar) => {
    if (!currentStampDraft || !currentUser) return;

    // Start loading state for the specific send
    setIsSendingDrafts(true);
    setSendingStatus('sending');

    try {
      // Send the current mail with the chosen stamp - using existing ID to edit instead of create new row
      await sendMail(
        currentUser.username,
        currentStampDraft.recipient || '',
        currentStampDraft.subject,
        currentStampDraft.content,
        currentStampDraft.backgroundColor,
        currentStampDraft.textColorVar,
        currentStampDraft.bgColorVar,
        stampChar,
        currentStampDraft.id,
        currentStampDraft.isHandwritten
      );

      // Move from Drafts to Sent for the sender
      await moveMailToFolderByName(currentStampDraft.id, 'Sent', currentUser.username);

      // Prepare for next in queue
      const remainingQueue = stampQueue.slice(1);

      if (remainingQueue.length > 0) {
        setStampQueue(remainingQueue);
        setCurrentStampDraft(remainingQueue[0]);
        // Keep selector open, but reset sending status since we are waiting for next stamp
        setSendingStatus(null);
        setIsSendingDrafts(false);
      } else {
        // Queue finished
        setStampQueue([]);
        setCurrentStampDraft(null);
        setShowStampSelector(false);
        setSendingStatus('sent');

        // Refresh data
        const updatedMails = await getMailsForUser(currentUser.username);
        const updatedFolders = await getFoldersForUser(currentUser.username);
        setMails(updatedMails);
        setFolders(updatedFolders);

        const inbox = updatedFolders.find(f => f.name === 'Inbox');
        if (inbox) setSelectedFolderId(inbox.id);

        setIsSendingDrafts(false);
        alert('All selected letters sent successfully!');
      }

      // Close mail if it was open
      if (selectedMail && selectedMail.id === currentStampDraft.id) {
        setSelectedMail(null);
      }
    } catch (error) {
      console.error('Error sending mail with stamp:', error);
      alert('Failed to send mail. Please try again.');
      setSendingStatus(null);
      setIsSendingDrafts(false);
    }
  };

  const cancelCompose = () => {
    setComposeForm({ to: '', subject: '', content: '' });
    if (contentRef.current) {
      contentRef.current.innerHTML = '';
    }
    bufferedContentRef.current = '';
    setIsComposing(false);
  };

  const transferMailsToInbox = async () => {
    setIsMailboxAnimating(true);
    setTimeout(() => setIsMailboxAnimating(false), 1000); // Animation duration

    const hiddenFolder = folders.find(f => f.name === 'Hidden');
    const inbox = folders.find(f => f.name === 'Inbox');
    if (!hiddenFolder || !inbox) return;

    // Get mailIds in hidden that are actual mails from Supabase AND are visible
    const hiddenMailIds = hiddenFolder.mailIds.filter(id => {
      const mail = mails.find(m => m.id === id);
      return mail && (!mail.hiddenUntil || new Date(mail.hiddenUntil) <= new Date());
    });

    // Move each to inbox persistently
    for (const mailId of hiddenMailIds) {
      await moveMailToFolderSupabase(mailId, inbox.id);
    }

    // Refresh data from Supabase
    const updatedMails = await getMailsForUser(currentUser.username);
    const updatedFolders = await getFoldersForUser(currentUser.username);
    setMails(updatedMails);
    setFolders(updatedFolders);

    // Sort the Inbox mailIds by sentAt descending (newest first)
    const inboxFolder = updatedFolders.find(f => f.name === 'Inbox');
    if (inboxFolder) {
      inboxFolder.mailIds.sort((a, b) => {
        const mailA = updatedMails.find(m => m.id === a);
        const mailB = updatedMails.find(m => m.id === b);
        return new Date(mailB.sentAt) - new Date(mailA.sentAt);
      });
    }

    // Switch to Inbox view
    setSelectedFolderId(inbox.id);
  };

  const sanitizeInput = (text) => {
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  };

  const handleCreateFolder = async () => {
    const cleanName = sanitizeInput(newFolderName.trim());
    await handleCreateFolderWithName(cleanName);
  };

  const handleCreateFolderWithName = async (name) => {
    const cleanName = sanitizeInput(name.trim());
    if (cleanName && currentUser) {
      try {
        const newFolder = await createFolder(currentUser.username, cleanName);
        setFolders(prev => [...prev, { ...newFolder, mailIds: [] }]);
        setNewFolderName('');
      } catch (error) {
        console.error('Error creating folder:', error);
        alert('Failed to create folder. Please try again.');
      }
    }
  };

  const moveMailToFolder = async (mailId, folderId) => {
    if (!currentUser) return;

    try {
      await moveMailToFolderSupabase(mailId, folderId);

      // Update local state
      setFolders(prev => prev.map(folder => {
        if (folder.id === folderId) {
          return { ...folder, mailIds: [...folder.mailIds, mailId] };
        } else if (folder.mailIds.includes(mailId)) {
          return { ...folder, mailIds: folder.mailIds.filter(id => id !== mailId) };
        }
        return folder;
      }));

      // Also remove from deleted folders if present
      setDeletedFolders(prev => prev.map(folder => {
        if (folder.mailIds.includes(mailId)) {
          return { ...folder, mailIds: folder.mailIds.filter(id => id !== mailId) };
        }
        return folder;
      }));

      // Refresh data from Supabase to ensure consistency
      const updatedMails = await getMailsForUser(currentUser.username);
      const updatedFolders = await getFoldersForUser(currentUser.username);
      setMails(updatedMails);
      setFolders(updatedFolders);
    } catch (error) {
      console.error('Error moving mail to folder:', error);
      alert('Failed to move mail to folder. Please try again.');
    }
  };

  const selectFolder = (folder) => {
    setSelectedFolderId(folder.id);
    setSelectedMail(null);
    setSelectedDeletedFolderId(null);
    setSearchTerm('');
  };

  const selectDeletedFolder = (folderId) => {
    setSelectedDeletedFolderId(folderId);
    setSelectedMail(null);
    setSearchTerm('');
  };

  const exitDeletedFolder = () => {
    setSelectedDeletedFolderId(null);
    setSelectedMail(null);
    setSearchTerm('');
  };

  const deleteFolder = async (folderId) => {
    if (!currentUser) return;
    const folderToDelete = folders.find(f => f.id === folderId);
    if (folderToDelete && folderToDelete.name !== 'Bin') {
      try {
        // Soft delete the folder in Supabase
        await softDeleteFolder(folderId);

        // NOTE: We no longer move mails to the Bin folder individualy.
        // They stay linked to the soft-deleted folder so they appear inside it in the Bin.

        // Refresh state
        const updatedMails = await getMailsForUser(currentUser.username);
        const updatedFolders = await getFoldersForUser(currentUser.username);
        const updatedDeletedFolders = await getDeletedFoldersForUser(currentUser.username);
        setMails(updatedMails);
        setFolders(updatedFolders);
        setDeletedFolders(updatedDeletedFolders);

        if (selectedFolderId === folderId) {
          const inbox = updatedFolders.find(f => f.name === 'Inbox');
          setSelectedFolderId(inbox ? inbox.id : null);
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
        alert('Failed to delete folder. Please try again.');
      }
      // If the deleted folder was selected, switch to Inbox
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null); // Inbox id
      }
    }
  };



  const confirmEmptyBin = () => {
    setShowConfirmEmptyBin(true);
  };

  const emptyBin = async () => {
    if (!currentUser) return;
    setIsEmptyingBin(true);
    try {
      // Remove all items in Bin folder from Supabase
      const binFolder = folders.find(f => f.name === 'Bin');
      if (binFolder && binFolder.mailIds.length > 0) {
        for (const mailId of binFolder.mailIds) {
          await deleteMailFromSupabase(mailId);
        }
      }

      // Also permanently delete all folders in the Bin
      if (deletedFolders.length > 0) {
        for (const folder of deletedFolders) {
          // This will remove links and delete the folder, but also delete the mails
          // We need to delete the mails inside too since they were intended for deletion
          for (const mailId of folder.mailIds) {
            await deleteMailFromSupabase(mailId);
          }
          await deleteFolderFromSupabase(folder.id);
        }
      }

      // Restore tracking for original folders
      setMailOriginalFolders({});

      // Refresh after deletion
      const updatedMails = await getMailsForUser(currentUser.username);
      const updatedFolders = await getFoldersForUser(currentUser.username);
      const updatedDeletedFolders = await getDeletedFoldersForUser(currentUser.username);
      setMails(updatedMails);
      setFolders(updatedFolders);
      setDeletedFolders(updatedDeletedFolders);

      setSelectedMail(null);
      setSelectedDeletedFolderId(null);
      setShowConfirmEmptyBin(false);
    } catch (error) {
      console.error('Error emptying bin:', error);
      alert('Failed to empty bin. Please try again.');
    } finally {
      setIsEmptyingBin(false);
    }
  };

  const cancelEmptyBin = () => {
    setShowConfirmEmptyBin(false);
  };

  const confirmRestoreFolder = (folderId) => {
    setFolderToRestore(folderId);
    setShowConfirmRestore(true);
  };

  const restoreFolder = async () => {
    const folderToRestoreObj = deletedFolders.find(f => f.id === folderToRestore);
    if (folderToRestoreObj && currentUser) {
      try {
        // Just restore the folder in Supabase
        await restoreFolderSupabase(folderToRestoreObj.id);

        // Mails are already in the folder, so we don't need to move them back from Bin.

        // Update local state after successful Supabase operations
        setFolders(prev => {
          let updatedFolders = prev.map(folder => {
            if (folder.name === 'Bin') {
              return { ...folder, mailIds: folder.mailIds.filter(id => !folderToRestoreObj.mailIds.includes(id)) };
            }
            return folder;
          });

          // Check if restoring a Drafts folder and if one already exists
          if (folderToRestoreObj.name === 'Drafts') {
            const existingDraftsFolder = updatedFolders.find(f => f.name === 'Drafts');
            if (existingDraftsFolder) {
              // Transfer mails to existing Drafts folder
              updatedFolders = updatedFolders.map(folder =>
                folder.name === 'Drafts'
                  ? { ...folder, mailIds: [...folder.mailIds, ...folderToRestoreObj.mailIds] }
                  : folder
              );
              // Do not add the restored folder back, just remove from deletedFolders
              setDeletedFolders(prev => prev.filter(f => f !== folderToRestoreObj));
              return updatedFolders;
            }
          }

          // Add the folder back if not merging Drafts
          return [...updatedFolders, folderToRestoreObj];
        });

        // Remove from deleted folders if not already removed in the Drafts case
        if (folderToRestoreObj.name !== 'Drafts' || !folders.find(f => f.name === 'Drafts')) {
          setDeletedFolders(prev => prev.filter(f => f !== folderToRestoreObj));
        }

        // Refresh data from Supabase to ensure consistency
        const updatedMails = await getMailsForUser(currentUser.username);
        const updatedFolders = await getFoldersForUser(currentUser.username);
        setMails(updatedMails);
        setFolders(updatedFolders);
      } catch (error) {
        console.error('Error restoring folder:', error);
        alert('Failed to restore folder. Please try again.');
      }
    }
    setShowConfirmRestore(false);
    setFolderToRestore(null);
  };

  const cancelRestoreFolder = () => {
    setShowConfirmRestore(false);
    setFolderToRestore(null);
  };

  const confirmDeleteMail = (mailId) => {
    setShowConfirmDelete(true);
  };

  const cancelDeleteMail = () => {
    setShowConfirmDelete(false);
  };

  const confirmRestoreMail = (mailId) => {
    setMailToRestore(mailId);
    setShowConfirmRestoreMail(true);
  };

  const cancelRestoreMail = () => {
    setShowConfirmRestoreMail(false);
    setMailToRestore(null);
  };

  const confirmDeleteFolder = () => {
    if (folderToDeleteId) {
      deleteFolder(folderToDeleteId);
      setShowConfirmDeleteFolder(false);
      setFolderToDeleteId(null);
    }
  };

  const cancelDeleteFolder = () => {
    setShowConfirmDeleteFolder(false);
    setFolderToDeleteId(null);
  };

  const confirmDeleteImage = () => {
    if (imageToDelete) {
      saveHistory();
      imageToDelete.remove();
      setSelectedImageWrapper(null);
      // Update content editor height after removing image
      setTimeout(() => updateContentEditorHeight(), 0);
      setShowConfirmDeleteImage(false);
      setImageToDelete(null);
      // Refocus the content editor
      if (contentRef.current) {
        contentRef.current.focus();
      }
    }
  };

  const cancelDeleteImage = () => {
    setShowConfirmDeleteImage(false);
    setImageToDelete(null);
    // Refocus the content editor
    if (contentRef.current) {
      contentRef.current.focus();
    }
  };

  const tutorialSteps = [
    {
      title: "Welcome to LetterBox! 📬",
      description: "Discover the Art of Slow Correspondence",
      content: "Experience a more meaningful way to connect. In LetterBox, letters don't arrive instantly—they take time to travel, making every message special.",
      icon: "✉️",
      target: null
    },
    {
      title: "The Postal Service",
      description: "Delayed Delivery & Hidden Folder",
      content: "This is where you receive your letters. The number on the icon indicates the number of letters in your Letterbox.",
      icon: "📪",
      target: "#tutorial-mailbox-icon",
      placement: 'bottom',
      padding: { top: -10, right: 30, bottom: 40, left: -5 } // Less on top and left
    },
    {
      title: "Your Organized Workspace",
      description: "Folders & Navigation",
      content: "Navigate through your records here. Letters from your Letterbox will transfer to your Inbox, and you can create your own folders to organize them.",
      icon: "📂",
      target: ".taskbar-folders",
      placement: 'top'
    },
    {
      title: "Writing with Care",
      description: "Letters, Fonts & Photos",
      content: "Click here to craft your own message. You can choose premium fonts like Lora and customize the design to suit your letter.",
      icon: "✍️",
      target: "#tutorial-compose-button",
      placement: 'bottom'
    },
    {
      title: "The Waste Bin",
      description: "Managing Your Space",
      content: "Deleted letters and folders end up here. You can choose to restore them if you change your mind, or clear them permanently.",
      icon: "🗑️",
      target: ".taskbar-bin-btn",
      placement: 'top'
    },
    {
      title: "You're All Set!",
      description: "Begin Your Journey",
      content: "Ready to start? Write your first letter or explore your mailbox. We hope you enjoy this more thoughtful way to communicate.",
      icon: "✨",
      target: null
    }
  ];

  const handleNextStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      finishTutorial();
    }
  };

  const handlePrevStep = () => {
    if (tutorialStep > 0) {
      setTutorialStep(tutorialStep - 1);
    }
  };

  const finishTutorial = async () => {
    setShowTutorial(false);
    setSpotlightRect(null);
    if (currentUser && currentUser.id) {
      try {
        await updateTutorialStatus(currentUser.id);
        setCurrentUser(prev => ({ ...prev, has_seen_tutorial: true }));
      } catch (err) {
        console.error('Failed to update tutorial status:', err);
      }
    }
  };

  useLayoutEffect(() => {
    if (showTutorial) {
      let currentTargetSelector = tutorialSteps[tutorialStep].target;

      // Dynamic target for folders step
      if (currentTargetSelector === '#folders-target') {
        currentTargetSelector = window.innerWidth > 768 ? "#tutorial-folder-list" : "#tutorial-folder-dropdown-btn";
      }

      if (currentTargetSelector) {
        const updateRect = () => {
          // Re-evaluate target selector on each update for responsive steps
          let activeSelector = tutorialSteps[tutorialStep].target;
          if (activeSelector === '#folders-target') {
            activeSelector = window.innerWidth > 768 ? "#tutorial-folder-list" : "#tutorial-folder-dropdown-btn";
          }

          const element = document.querySelector(activeSelector);
          if (element) {
            const rect = element.getBoundingClientRect();
            const rawPadding = tutorialSteps[tutorialStep].padding || 8;
            const p = typeof rawPadding === 'number'
              ? { top: rawPadding, right: rawPadding, bottom: rawPadding, left: rawPadding }
              : rawPadding;

            setSpotlightRect({
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              padding: p
            });
          }
        };

        updateRect();
        const resizeHandler = () => updateRect();
        const scrollHandler = () => updateRect();

        window.addEventListener('resize', resizeHandler);
        window.addEventListener('scroll', scrollHandler, true); // Capture phase for better scroll tracking

        return () => {
          window.removeEventListener('resize', resizeHandler);
          window.removeEventListener('scroll', scrollHandler, true);
        };
      } else {
        setSpotlightRect(null);
      }
    }
  }, [showTutorial, tutorialStep]);

  const deleteMail = async (mailId) => {
    // Find the original folder before moving to Bin
    const originalFolder = folders.find(f => f.mailIds.includes(mailId));
    if (originalFolder) {
      setMailOriginalFolders(prev => ({
        ...prev,
        [mailId]: originalFolder.name
      }));
    }

    // Move mail to Bin folder and remove empty Drafts folder if applicable
    setFolders(prev => {
      let updatedFolders = prev.map(folder => {
        if (folder.name === 'Bin') {
          return { ...folder, mailIds: [...folder.mailIds, mailId] };
        } else if (folder.mailIds.includes(mailId)) {
          return { ...folder, mailIds: folder.mailIds.filter(id => id !== mailId) };
        }
        return folder;
      });

      // If the original folder was Drafts and it's now empty, remove it
      if (originalFolder && originalFolder.name === 'Drafts') {
        const draftsFolder = updatedFolders.find(f => f.name === 'Drafts');
        if (draftsFolder && draftsFolder.mailIds.length === 0) {
          updatedFolders = updatedFolders.filter(f => f.name !== 'Drafts');
          // If Drafts folder was selected, switch to Inbox
          if (selectedFolderId === draftsFolder.id) {
            const inbox = folders.find(f => f.name === 'Inbox');
            if (inbox) {
              setSelectedFolderId(inbox.id);
            }
          }
        }
      }

      return updatedFolders;
    });
    setSelectedMail(null);

    // Move to Bin in Supabase
    if (currentUser) {
      try {
        const binFolder = folders.find(f => f.name === 'Bin');
        if (binFolder) {
          await moveMailToFolderSupabase(mailId, binFolder.id);
        }
      } catch (error) {
        console.error('Error moving mail to Bin in Supabase:', error);
      }
    }
  };

  const restoreMail = async (mailId) => {
    // Determine original folder: check deleted folders first, then mailOriginalFolders
    const originalDeletedFolder = deletedFolders.find(f => f.mailIds.includes(mailId));
    const originalFolderName = originalDeletedFolder ? originalDeletedFolder.name : mailOriginalFolders[mailId];
    let targetFolderName = originalFolderName;

    // Determine target folder name
    const existingFolder = folders.find(f => f.name === originalFolderName);
    if (originalFolderName === 'Drafts' && !existingFolder && currentUser) {
      // Create a new Drafts folder in Supabase first
      try {
        await createFolder(currentUser.username, 'Drafts');
      } catch (error) {
        console.error('Error creating Drafts folder:', error);
        alert('Failed to restore mail. Please try again.');
        return;
      }
    } else if (!existingFolder) {
      // Default to Inbox
      targetFolderName = 'Inbox';
    }

    // Move to target folder in Supabase first
    if (currentUser && targetFolderName) {
      try {
        await moveMailToFolderByName(mailId, targetFolderName, currentUser.username);

        // Update local state only after successful Supabase operation
        setFolders(prev => {
          let updatedFolders = prev;
          if (originalFolderName === 'Drafts' && !existingFolder) {
            // Add the new Drafts folder (will be refreshed from Supabase)
            updatedFolders = [...updatedFolders, {
              id: `temp-${Date.now()}`, // Temporary ID, will be replaced by refresh
              name: 'Drafts',
              mailIds: [mailId]
            }];
          }

          // Move mail from Bin to target folder
          updatedFolders = updatedFolders.map(folder => {
            if (folder.name === 'Bin') {
              return { ...folder, mailIds: folder.mailIds.filter(id => id !== mailId) };
            } else if (folder.name === targetFolderName && !folder.mailIds.includes(mailId)) {
              return { ...folder, mailIds: [...folder.mailIds, mailId] };
            }
            return folder;
          });

          return updatedFolders;
        });

        // Also remove from deleted folders if present
        setDeletedFolders(prev => prev.map(folder => {
          if (folder.mailIds.includes(mailId)) {
            return { ...folder, mailIds: folder.mailIds.filter(id => id !== mailId) };
          }
          return folder;
        }));
        // Clean up the original folder tracking
        setMailOriginalFolders(prev => {
          const updated = { ...prev };
          delete updated[mailId];
          return updated;
        });
        setSelectedMail(null);

        // Refresh data from Supabase to ensure consistency
        const updatedMails = await getMailsForUser(currentUser.username);
        const updatedFolders = await getFoldersForUser(currentUser.username);
        setMails(updatedMails);
        setFolders(updatedFolders);
      } catch (error) {
        console.error('Error moving mail back to folder in Supabase:', error);
        alert('Failed to restore mail. Please try again.');
      }
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (contentRef.current) {
      contentRef.current.focus();
    }
    // Update toolbar states after applying formatting
    getSelectedTextFormatting();
  };

  // Helper function to wrap text nodes with color
  const wrapTextNodesWithColor = (element, color) => {
    const selection = window.getSelection();
    let cursorTextNode = null;
    if (selection.rangeCount > 0 && selection.isCollapsed) {
      const container = selection.getRangeAt(0).startContainer;
      cursorTextNode = container.nodeType === Node.TEXT_NODE ? container : null;
    }

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }
    textNodes.forEach(textNode => {
      if (textNode === cursorTextNode) return; // Skip wrapping the text node at cursor
      let parent = textNode.parentNode;
      let hasExplicitColor = false;
      while (parent && parent !== element) {
        if (parent.style && parent.style.color && parent.style.color !== '') {
          hasExplicitColor = true;
          break;
        }
        parent = parent.parentNode;
      }
      if (!hasExplicitColor) {
        const currentColor = window.getComputedStyle(textNode.parentNode).color;
        const span = document.createElement('span');
        span.style.color = currentColor;
        textNode.parentNode.insertBefore(span, textNode);
        span.appendChild(textNode);
      }
    });
  };

  // Helper function to wrap text nodes with background
  const wrapTextNodesWithBackground = (element, bg) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }
    textNodes.forEach(textNode => {
      if (textNode.parentNode.tagName !== 'SPAN' || !textNode.parentNode.style.backgroundColor) {
        const span = document.createElement('span');
        span.style.backgroundColor = bg;
        textNode.parentNode.insertBefore(span, textNode);
        span.appendChild(textNode);
      }
    });
  };

  // Function to get the formatting from selected text or cursor position
  const getSelectedTextFormatting = () => {
    if (getFormattingTimeoutRef.current) {
      clearTimeout(getFormattingTimeoutRef.current);
    }
    getFormattingTimeoutRef.current = setTimeout(() => {
      performFormattingCheck();
    }, 300);
  };

  const performFormattingCheck = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    const commonAncestor = range.commonAncestorContainer;
    const editor = contentRef.current;
    if (!editor || document.activeElement !== editor) {
      return;
    }

    if (!editor.contains(commonAncestor)) {
      return;
    }

    let textNodes = [];
    if (selection.isCollapsed) {
      // For collapsed selection, get the text node at cursor
      const container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        textNodes.push(container);
      } else if (container.nodeType === Node.ELEMENT_NODE) {
        // If cursor is in an element, find the text node
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        let node;
        while (node = walker.nextNode()) {
          textNodes.push(node);
          break; // Just the first one
        }
      }
    } else {
      // Get all text nodes within the selection using original DOM
      const iterator = document.createNodeIterator(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
          }
        }
      );

      let node;
      while (node = iterator.nextNode()) {
        textNodes.push(node);
      }

      // If no text nodes, try to get from the range itself
      if (textNodes.length === 0) {
        const container = range.startContainer;
        if (container.nodeType === Node.TEXT_NODE) {
          textNodes.push(container);
        }
      }
    }

    // Analyze formatting for each text node or the current element if collapsed
    const formattingCounts = {
      fontFamily: {},
      fontSize: {},
      lineHeight: {},
      fontWeight: {},
      fontStyle: {},
      textDecoration: {},
      color: {},
      backgroundColor: {}
    };

    if (textNodes.length === 0 && selection.isCollapsed) {
      // Analyze the element hierarchy at the cursor if no text nodes found (e.g. at start of empty line)
      let element = range.startContainer.nodeType === Node.ELEMENT_NODE ? range.startContainer : range.startContainer.parentElement;
      while (element && element !== editor) {
        if (element.style) {
          const computedStyle = window.getComputedStyle(element);
          const rawFontFamily = computedStyle.fontFamily.split(',')[0].replace(/['"]/g, '').toLowerCase();
          const normalizedFontFamily = rawFontFamily.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          if (normalizedFontFamily) formattingCounts.fontFamily[normalizedFontFamily] = 1;

          const fontSizePx = parseFloat(computedStyle.fontSize);
          const fontSizeMap = { 10: '1', 13: '2', 16: '3', 18: '4', 24: '5', 32: '6', 48: '7' };
          const normalizedFontSize = Object.keys(fontSizeMap).reduce((prev, curr) => Math.abs(curr - fontSizePx) < Math.abs(prev - fontSizePx) ? curr : prev, 16);
          const fontSizeValue = fontSizeMap[normalizedFontSize];
          if (fontSizeValue) formattingCounts.fontSize[fontSizeValue] = 1;

          const color = computedStyle.color;
          if (color) formattingCounts.color[color] = 1;
          const backgroundColor = computedStyle.backgroundColor;
          if (backgroundColor) formattingCounts.backgroundColor[backgroundColor] = 1;
        }
        element = element.parentElement;
      }
    }



    textNodes.forEach(textNode => {
      let element = textNode.parentElement;
      while (element && element !== editor) {
        const computedStyle = window.getComputedStyle(element);

        // Font family (normalize to match select options)
        const rawFontFamily = computedStyle.fontFamily.split(',')[0].replace(/['"]/g, '').toLowerCase();
        const normalizedFontFamily = rawFontFamily.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        formattingCounts.fontFamily[normalizedFontFamily] = (formattingCounts.fontFamily[normalizedFontFamily] || 0) + 1;

        // Font size (normalize to select values)
        const fontSizePx = parseFloat(computedStyle.fontSize);
        const fontSizeMap = { 10: '1', 13: '2', 16: '3', 18: '4', 24: '5', 32: '6', 48: '7' };
        const normalizedFontSize = Object.keys(fontSizeMap).reduce((prev, curr) =>
          Math.abs(curr - fontSizePx) < Math.abs(prev - fontSizePx) ? curr : prev
        );
        const fontSizeValue = fontSizeMap[normalizedFontSize] || '';
        formattingCounts.fontSize[fontSizeValue] = (formattingCounts.fontSize[fontSizeValue] || 0) + 1;

        // Line height (normalize to ratio)
        const lineHeightValue = parseFloat(computedStyle.lineHeight);
        const lineHeightRatio = isNaN(lineHeightValue) ? 1.2 : lineHeightValue;
        let normalizedLineHeight = '';
        if (lineHeightRatio >= 0.95 && lineHeightRatio <= 1.05) {
          normalizedLineHeight = '1';
        } else if (lineHeightRatio >= 1.1 && lineHeightRatio <= 1.2) {
          normalizedLineHeight = '1.15';
        } else if (lineHeightRatio >= 1.4 && lineHeightRatio <= 1.6) {
          normalizedLineHeight = '1.5';
        } else if (lineHeightRatio >= 1.9 && lineHeightRatio <= 2.1) {
          normalizedLineHeight = '2';
        }
        if (normalizedLineHeight) {
          formattingCounts.lineHeight[normalizedLineHeight] = (formattingCounts.lineHeight[normalizedLineHeight] || 0) + 1;
        }

        // Font weight (bold) - use queryCommandState for accuracy
        // Font style (italic) - use queryCommandState for accuracy
        // Text decoration (underline) - use queryCommandState for accuracy
        // These are handled separately below

        // Color
        const color = computedStyle.color;
        formattingCounts.color[color] = (formattingCounts.color[color] || 0) + 1;

        // Background color
        const backgroundColor = computedStyle.backgroundColor;
        formattingCounts.backgroundColor[backgroundColor] = (formattingCounts.backgroundColor[backgroundColor] || 0) + 1;

        element = element.parentElement;
      }
    });

    // Use queryCommandState for bold, italic, underline, and alignments as they are more reliable
    const isBold = document.queryCommandState('bold');
    const isItalic = document.queryCommandState('italic');
    const isUnderline = document.queryCommandState('underline');
    const isAlignLeft = document.queryCommandState('justifyLeft');
    const isAlignCenter = document.queryCommandState('justifyCenter');
    const isAlignRight = document.queryCommandState('justifyRight');
    const isAlignJustify = document.queryCommandState('justifyFull');

    // Find majority for other formatting types
    const getMajority = (counts) => {
      let maxCount = 0;
      let majority = '';
      for (const [key, count] of Object.entries(counts)) {
        if (count > maxCount) {
          maxCount = count;
          majority = key;
        }
      }
      return majority;
    };

    // For colors, convert to hex if needed
    const majorityTextColor = getMajority(formattingCounts.color);
    const majorityHighlightColor = getMajority(formattingCounts.backgroundColor);

    // Whole letter background color (from editor's backgroundColor)
    const editorBackgroundColor = editor.style.backgroundColor || window.getComputedStyle(editor).backgroundColor;
    const majorityWholeLetterBackgroundColor = editorBackgroundColor ? rgbaToHex(editorBackgroundColor) : '#ffffff';

    // Use queryCommandValue for font and size as it's more accurate for the current cursor position/selection
    const currentFont = document.queryCommandValue('fontName').replace(/['"]/g, '');
    const normalizedFont = currentFont.split(',')[0].trim().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    // Convert size to normalized map value
    const currentFontSize = document.queryCommandValue('fontSize');

    const nextFormatting = {
      font: normalizedFont || getMajority(formattingCounts.fontFamily),
      fontSize: currentFontSize || getMajority(formattingCounts.fontSize),
      lineHeight: getMajority(formattingCounts.lineHeight) || editorFormatting.lineHeight,
      bold: isBold,
      italic: isItalic,
      underline: isUnderline,
      alignLeft: isAlignLeft,
      alignCenter: isAlignCenter,
      alignRight: isAlignRight,
      alignJustify: isAlignJustify,
      textColor: majorityTextColor ? rgbaToHex(majorityTextColor) : '#000000',
      highlightColor: majorityHighlightColor ? rgbaToHex(majorityHighlightColor) : '#ffffff',
      wholeLetterBackgroundColor: majorityWholeLetterBackgroundColor
    };


    // Only update if something actually changed to prevent redundant re-renders
    const hasChanged = Object.keys(nextFormatting).some(key => nextFormatting[key] !== editorFormatting[key]);
    if (hasChanged) {
      setEditorFormatting(nextFormatting);
    }
  };

  // Helper function to convert rgba to hex
  const rgbaToHex = (rgba) => {
    if (rgba.startsWith('#')) return rgba;
    if (rgba === 'transparent' || rgba === 'rgba(0, 0, 0, 0)') return '#ffffff';

    const match = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
    if (!match) return '#000000';

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const handleFontChange = (e) => {
    const font = e.target.value;
    const isEditorEmpty = !contentRef.current ||
      contentRef.current.innerHTML.trim() === '' ||
      contentRef.current.innerHTML === '<br>' ||
      contentRef.current.innerHTML === '<p><br></p>';

    if (contentRef.current && isEditorEmpty) {
      contentRef.current.style.setProperty('--editor-font', font);
    }
    execCommand('fontName', font);
    setEditorFormatting(prev => ({ ...prev, font }));
  };

  const handleFontSizeChange = (e) => {
    const size = e.target.value;
    const sizeMap = { '1': '10px', '2': '13px', '3': '16px', '4': '18px', '5': '24px', '6': '32px', '7': '48px' };
    const isEditorEmpty = !contentRef.current ||
      contentRef.current.innerHTML.trim() === '' ||
      contentRef.current.innerHTML === '<br>' ||
      contentRef.current.innerHTML === '<p><br></p>';

    if (contentRef.current && isEditorEmpty) {
      contentRef.current.style.setProperty('--editor-size', sizeMap[size] || '16px');
    }
    if (size) {
      execCommand('fontSize', size);
      setEditorFormatting(prev => ({ ...prev, fontSize: size }));
    }
  };




  const handleTextColorChange = (color) => {
    if (color) {
      if (contentRef.current) {
        contentRef.current.focus();
        document.execCommand('foreColor', false, color);
        getSelectedTextFormatting();
      }
      setEditorFormatting(prev => ({ ...prev, textColor: color }));
      setComposeForm(prev => ({ ...prev, textColorVar: color }));
    }
  };

  const handleHighlightColorChange = (color) => {
    if (color && contentRef.current) {
      contentRef.current.focus();

      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        // Apply to selected text
        document.execCommand('backColor', false, color);
      } else {
        // Wrap existing text with current background to preserve them
        wrapTextNodesWithBackground(contentRef.current, 'transparent');
        // Set background for future text
        contentRef.current.style.setProperty('--bg-color', color);
      }
      setEditorFormatting(prev => ({ ...prev, highlightColor: color }));
    }
  };

  const applyHighlightColorToWholeLetter = (color) => {
    if (color && contentRef.current) {
      contentRef.current.style.backgroundColor = color;
      contentRef.current.style.setProperty('--custom-bg', color);
      // Don't override individual span background colors - let them show on top
      // Only spans with explicit background colors should keep their highlight
    }
  };

  const handleWholeLetterBackgroundColorChange = (color) => {
    if (color) {
      applyHighlightColorToWholeLetter(color);
      setEditorFormatting(prev => ({ ...prev, wholeLetterBackgroundColor: color }));
      setComposeForm(prev => ({ ...prev, backgroundColor: color }));
    }
  };

  const handleWholeLetterHighlightColorChange = (e) => {
    const color = e.target.value;
    applyHighlightColorToWholeLetter(color);
  };





  const insertList = (type) => {
    const command = type === 'unordered' ? 'insertUnorderedList' : 'insertOrderedList';
    execCommand(command);
  };

  const handleLineSpacingChange = (e) => {
    const spacing = e.target.value;
    if (spacing && contentRef.current) {
      contentRef.current.focus();

      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        // Apply to selected text
        const range = selection.getRangeAt(0);
        const span = document.createElement('span');
        span.style.lineHeight = spacing;

        try {
          range.surroundContents(span);
          // Restore selection
          selection.removeAllRanges();
          const newRange = document.createRange();
          newRange.selectNodeContents(span);
          selection.addRange(newRange);
        } catch (e) {
          // If surroundContents fails, extract and replace
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      } else {
        // Apply to current line/paragraph or create new one
        const range = selection.getRangeAt(0);
        let currentNode = range.commonAncestorContainer;
        if (currentNode.nodeType === Node.TEXT_NODE) {
          currentNode = currentNode.parentNode;
        }

        // Find the current paragraph or line
        let paragraph = currentNode;
        while (paragraph && paragraph !== contentRef.current && paragraph.tagName !== 'P' && paragraph.tagName !== 'DIV') {
          paragraph = paragraph.parentNode;
        }

        if (paragraph && paragraph !== contentRef.current) {
          paragraph.style.lineHeight = spacing;
        } else {
          // If no paragraph found, create a new paragraph with the selected line height
          const p = document.createElement('p');
          p.style.lineHeight = spacing;
          const br = document.createElement('br');
          p.appendChild(br);
          range.insertNode(p);
          // Move cursor into the new paragraph
          selection.removeAllRanges();
          const newRange = document.createRange();
          newRange.setStart(p, 0);
          newRange.setEnd(p, 0);
          selection.addRange(newRange);
        }
      }
      // Set the line height on the editor for future text
      contentRef.current.style.lineHeight = spacing;
      contentRef.current.style.lineHeight = spacing;
      setEditorFormatting(prev => ({ ...prev, lineHeight: spacing }));
    } else {
      setEditorFormatting(prev => ({ ...prev, lineHeight: '' }));
    }
  };

  const handleAttachPhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imgSrc = event.target.result;

        // Create a wrapper div for the image
        const wrapperDiv = document.createElement('div');
        wrapperDiv.className = 'image-wrapper';
        wrapperDiv.style.position = 'absolute';
        wrapperDiv.style.zIndex = imageZIndexCounter;
        wrapperDiv.contentEditable = false;
        // Add click handler to select the image
        wrapperDiv.addEventListener('click', () => setSelectedImageWrapper(wrapperDiv));

        // Increment z-index counter for next image
        setImageZIndexCounter(prev => prev + 1);

        const imgElement = document.createElement('img');
        imgElement.src = imgSrc;
        imgElement.alt = 'Attached Photo';
        imgElement.className = 'resizable-image';
        imgElement.style.width = '200px';
        imgElement.style.height = 'auto';
        imgElement.style.display = 'block';
        imgElement.style.cursor = 'move';
        imgElement.draggable = false;
        imgElement.contentEditable = false;

        const resizeHandle = document.createElement('div');
        resizeHandle.className = 'resize-handle';
        resizeHandle.style.position = 'absolute';
        resizeHandle.style.bottom = '0';
        resizeHandle.style.right = '0';
        resizeHandle.style.width = '15px';
        resizeHandle.style.height = '15px';
        resizeHandle.style.background = '#667eea';
        resizeHandle.style.borderRadius = '50%';
        resizeHandle.style.cursor = 'nw-resize';

        wrapperDiv.appendChild(imgElement);
        wrapperDiv.appendChild(resizeHandle);

        if (contentRef.current) {
          contentRef.current.focus();
          const selection = window.getSelection();

          // Create a clearfix div to reserve space and prevent text overlap
          const clearfix = document.createElement('div');
          clearfix.className = 'image-clearfix';
          clearfix.contentEditable = false;
          clearfix.style.height = '210px'; // Slightly more than default image height
          clearfix.style.clear = 'both';
          clearfix.style.pointerEvents = 'none';

          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            // Insert the clearfix at cursor position
            range.insertNode(clearfix);

            // Move cursor after the clearfix
            range.setStartAfter(clearfix);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            contentRef.current.appendChild(clearfix);
          }

          // Add the absolute wrapper to the editor
          contentRef.current.appendChild(wrapperDiv);

          // Position image at the clearfix's location
          setTimeout(() => {
            const clearfixRect = clearfix.getBoundingClientRect();
            const editorRect = contentRef.current.getBoundingClientRect();

            const DESIGN_CONTENT_WIDTH = 610; // 650 - 25 - 15
            const editorWidth = contentRef.current.clientWidth || 650;
            const currentContentWidth = editorWidth - 40;

            if (clearfixRect.top > 0) {
              const x = clearfixRect.left - editorRect.left;
              const innerX = x - 25;
              const P = (innerX / currentContentWidth) * 100;
              const normalizedInnerX = (innerX / currentContentWidth) * DESIGN_CONTENT_WIDTH;

              const offset = 25 - (P * 0.4);
              const pxLimit = 25 + normalizedInnerX;

              wrapperDiv.style.left = `min(${pxLimit.toFixed(2)}px, calc(${P.toFixed(2)}% + ${offset.toFixed(2)}px))`;
              wrapperDiv.style.top = `${clearfixRect.top - editorRect.top}px`;
            } else {
              const usableWidth = currentContentWidth;
              const centerX_content = Math.max(0, (usableWidth - 200) / 2);
              const P = (centerX_content / usableWidth) * 100;
              const normalizedInnerX = (centerX_content / usableWidth) * DESIGN_CONTENT_WIDTH;

              const offset = 25 - (P * 0.4);
              const pxLimit = 25 + normalizedInnerX;

              wrapperDiv.style.left = `min(${pxLimit.toFixed(2)}px, calc(${P.toFixed(2)}% + ${offset.toFixed(2)}px))`;
              wrapperDiv.style.top = `150px`;
            }

            wrapperDiv.style.width = 'fit-content';
            wrapperDiv.style.maxWidth = 'calc(100% - 25px)';

            updateContentEditorHeight();
            saveHistory();
          }, 50);
        }
      };
      reader.readAsDataURL(file);
    }
    // Reset the input
    e.target.value = '';
  };

  const handleImageMouseDown = (e) => {
    // Check if the target is an image or its wrapper
    const wrapper = e.target.closest('.image-wrapper');
    if (wrapper) {
      e.preventDefault();
      const editor = contentRef.current;
      if (!editor) return;

      const img = wrapper.querySelector('.resizable-image');
      const resizeHandle = wrapper.querySelector('.resize-handle');

      if (e.target === resizeHandle) {
        // Start resizing from the bottom-right corner
        const wrapperRect = wrapper.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();
        setResizingImage(img);
        setResizeDirection('bottom-right');
        setOriginalDimensions({ width: img.offsetWidth, height: img.offsetHeight });
        setInitialMouseX(e.clientX);
        setInitialMouseY(e.clientY);
        setInitialWrapperLeft(wrapperRect.left - editorRect.left);
        setInitialWrapperTop(wrapperRect.top - editorRect.top);
        wrapper.style.position = 'absolute';
        wrapper.style.left = `${wrapperRect.left - editorRect.left}px`;
        wrapper.style.top = `${wrapperRect.top - editorRect.top}px`;
        setIsDragging(true);
      } else {
        // Start dragging - calculate offset from mouse to wrapper's current position
        const wrapperRect = wrapper.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - wrapperRect.left,
          y: e.clientY - wrapperRect.top
        });
        wrapper.classList.add('dragging');
        setIsDragging(true);
      }
      // Set the selected image wrapper
      setSelectedImageWrapper(wrapper);
    }
  };

  const handleContentChange = (e) => {
    const currentHtml = e.target.innerHTML;
    bufferedContentRef.current = currentHtml;
    debouncedSaveHistory();
  };

  const syncBufferedContent = () => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    setComposeForm(prev => ({
      ...prev,
      content: bufferedContentRef.current
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Debounce history save on Enter to avoid blocking the UI during newline creation
      debouncedSaveHistory();
    }
  };

  const updateContentEditorHeight = () => {
    if (contentRef.current) {
      const editor = contentRef.current;
      const images = editor.querySelectorAll('.resizable-image');
      let maxBottom = 0;

      images.forEach(img => {
        const wrapper = img.parentElement;
        const top = parseFloat(wrapper.style.top) || 0;
        const height = parseFloat(wrapper.style.height) || img.offsetHeight;
        const bottom = top + height;
        maxBottom = Math.max(maxBottom, bottom);
      });

      // Set minimum height based on content only (no extra margin)
      const minHeight = 200;
      const newHeight = Math.max(minHeight, maxBottom);
      editor.style.minHeight = `${newHeight}px`;
    }
  };

  const hiddenFolder = folders.find(f => f.name === 'Hidden');
  const hiddenMailCount = hiddenFolder ? hiddenFolder.mailIds.filter(id => {
    const mail = mails.find(m => m.id === id);
    return mail && (!mail.hiddenUntil || new Date(mail.hiddenUntil) <= new Date());
  }).length : 0;

  // Initial Loading Page Component (for font loading)
  const InitialLoadingPage = () => (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
      opacity: isInitializing ? 1 : 0,
      transition: 'opacity 0.8s ease-out'
    }}>



      <h1 style={{
        color: '#2c3e50',
        fontSize: '3em',
        fontWeight: '200',
        textAlign: 'center',
        marginBottom: '15px',
        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
        letterSpacing: '2px'
      }}>
        LETTERBOX
      </h1>
      <p style={{
        color: '#7f8c8d',
        fontSize: '1.1em',
        textAlign: 'center',
        marginBottom: '40px',
        fontWeight: '300'
      }}>
        Setting up your workspace...
      </p>
      <div style={{
        display: 'flex',
        gap: '8px'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          animation: 'bounce 1.4s ease-in-out infinite both'
        }}></div>
        <div style={{
          width: '8px',
          height: '8px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          animation: 'bounce 1.4s ease-in-out 0.16s infinite both'
        }}></div>
        <div style={{
          width: '8px',
          height: '8px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          animation: 'bounce 1.4s ease-in-out 0.32s infinite both'
        }}></div>
      </div>
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-10deg); }
        }
        @keyframes float3 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-25px) rotate(15deg); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </div>
  );

  // Loading Page Component
  const LoadingPage = () => (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        fontSize: '4em',
        marginBottom: '20px',
        animation: 'bounce 2s infinite'
      }}>
        📬
      </div>
      <h1 style={{
        color: '#2c3e50',
        fontSize: '2.5em',
        fontWeight: '300',
        textAlign: 'center',
        marginBottom: '10px'
      }}>
        Welcome back, {currentUser?.username}!
      </h1>
      <p style={{
        color: '#7f8c8d',
        fontSize: '1.2em',
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        Preparing your mailbox...
      </p>
      <div style={{
        width: '200px',
        height: '4px',
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          animation: 'loadingBar 1.5s ease-in-out infinite'
        }}></div>
      </div>
      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );

  // Sending Drafts Loading Page Component
  const SendingDraftsLoadingPage = () => (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        fontSize: '4em',
        marginBottom: '20px',
        animation: sendingStatus === 'sending' ? 'bounce 2s infinite' : 'none'
      }}>
        📬
      </div>
      <h1 style={{
        color: '#2c3e50',
        fontSize: '2.5em',
        fontWeight: '300',
        textAlign: 'center',
        marginBottom: '10px'
      }}>
        {sendingStatus === 'sending' ? 'Sending Drafts...' : 'Drafts Sent!'}
      </h1>
      <p style={{
        color: '#7f8c8d',
        fontSize: '1.2em',
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        {sendingStatus === 'sending' ? 'Mails are being sent' : 'Mails have been sent'}
      </p>
      {sendingStatus === 'sending' && (
        <div style={{
          width: '200px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            animation: 'loadingBar 1.5s ease-in-out infinite'
          }}></div>
        </div>
      )}
      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );

  // Emptying Bin Loading Page Component
  const EmptyingBinLoadingPage = () => (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        fontSize: '4em',
        marginBottom: '20px',
        animation: 'shake 0.5s ease-in-out infinite'
      }}>
        🗑️
      </div>
      <h1 style={{
        color: '#2c3e50',
        fontSize: '2.5em',
        fontWeight: '300',
        textAlign: 'center',
        marginBottom: '10px'
      }}>
        Emptying Bin...
      </h1>
      <p style={{
        color: '#7f8c8d',
        fontSize: '1.2em',
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        Permanently deleting all items
      </p>
      <div style={{
        width: '200px',
        height: '4px',
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, #f44336 0%, #d32f2f 100%)',
          animation: 'loadingBar 1.5s ease-in-out infinite'
        }}></div>
      </div>
      <style>{`
        @keyframes shake {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          50% { transform: rotate(10deg); }
          75% { transform: rotate(-10deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );

  // Verification Success Page Component
  const VerifiedPage = () => {
    const navigate = useNavigate();
    return (
      <div className="auth-container" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div className="auth-card" style={{
          textAlign: 'center',
          animation: 'modalSlideUp 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)'
        }}>
          <div style={{
            fontSize: '6em',
            marginBottom: '20px',
            animation: 'popIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            ✅
          </div>
          <div className="auth-header">
            <h1 style={{
              color: '#2c3e50',
              fontSize: '2.5em',
              fontWeight: '300',
              marginBottom: '10px'
            }}>
              Verified Successfully!
            </h1>
            <p style={{
              color: '#7f8c8d',
              fontSize: '1.2em',
              lineHeight: '1.6'
            }}>
              Your account is now active.
            </p>
          </div>
          <p style={{
            color: '#7f8c8d',
            fontSize: '1.1em',
            margin: '30px 0',
            lineHeight: '1.6',
            maxWidth: '400px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            You can now log in to your Daily Mailbox and start sending letters across the world.
          </p>
          <button
            className="auth-button"
            onClick={() => navigate('/')}
            style={{
              maxWidth: '250px',
              margin: '20px auto 0',
              display: 'block'
            }}
          >
            Go to Login
          </button>
        </div>
        <style>{`
          @keyframes popIn {
            0% { transform: scale(0); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes modalSlideUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        <Route path="/verified" element={<VerifiedPage />} />
        <Route path="*" element={
          isInitializing ? <InitialLoadingPage /> :
            !isLoggedIn ? (
              <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: showLogin ? 1 : 0,
                transition: 'opacity 1s ease-in'
              }}>
                {isSignup ? (
                  <Signup
                    onInitiateVerification={(email) => {
                      alert(`Verification email sent to ${email}. Please check your inbox!`);
                      setIsSignup(false);
                    }}
                    onSwitchToLogin={handleSwitchToLogin}
                  />
                ) : (
                  <Login onLogin={handleLogin} onSwitchToSignup={handleSwitchToSignup} />
                )}
              </div>
            ) :
              isLoadingData ? <LoadingPage /> :
                showStampSelector && currentStampDraft ? (
                  <div className="stamp-popup-overlay">
                    <div className="stamp-popup-content">
                      <h3>Choose a Stamp for your Letter</h3>
                      <p className="stamp-popup-subtitle">To: {currentStampDraft.recipient || 'Unknown'}</p>

                      <div className="letter-preview-container">
                        <div className="letter-preview">
                          <div className="preview-envelope">
                            <div className="preview-to">To: {currentStampDraft.recipient || 'Unknown'}</div>
                            <div className="preview-subject">{currentStampDraft.subject ? (currentStampDraft.isHandwritten ? 'Handwritten Letter' : currentStampDraft.subject) : 'No Subject'}</div>
                            {hoveredStamp && (
                              <div className="preview-stamp-overlay" key={hoveredStamp}>
                                {hoveredStamp}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="stamps-grid">
                        {[
                          { id: 'classic', char: '🌸', name: 'Classic Post' },
                          { id: 'nature', char: '🌿', name: 'Nature Forest' },
                          { id: 'ocean', char: '🌊', name: 'Deep Sea' },
                          { id: 'moon', char: '🌙', name: 'Night Sky' },
                          { id: 'mountain', char: '🏔️', name: 'Alpine peak' },
                          { id: 'heart', char: '❤️', name: 'With Love' },
                          { id: 'royal', char: '👑', name: 'Royal Seal' },
                          { id: 'bird', char: '🕊️', name: 'Peace Dove' },
                        ].map((stamp) => (
                          <div
                            key={stamp.id}
                            className={`stamp-option ${hoveredStamp === stamp.char ? 'hovered' : ''}`}
                            onClick={() => {
                              handleStampChoice(stamp.char);
                              setHoveredStamp(null);
                            }}
                            onMouseEnter={() => setHoveredStamp(stamp.char)}
                            onMouseLeave={() => setHoveredStamp(null)}
                          >
                            <div className="stamp-design">{stamp.char}</div>
                            <span>{stamp.name}</span>
                          </div>
                        ))}
                      </div>
                      <button
                        className="cancel-btn"
                        onClick={() => {
                          setShowStampSelector(false);
                          setStampQueue([]);
                          setCurrentStampDraft(null);
                          setHoveredStamp(null);
                          setSendingStatus(null);
                          setIsSendingDrafts(false);
                        }}
                      >
                        Cancel Sending
                      </button>
                    </div>
                  </div>
                ) :
                  isSendingDrafts ? <SendingDraftsLoadingPage /> :
                    isEmptyingBin ? <EmptyingBinLoadingPage /> :
                      <>
                        <div className={`App ${isAnyPopupOpen ? 'popup-open' : ''}`}>
                          <header className="App-header">
                            <div className="header-left">
                              <span id="tutorial-mailbox-icon" className={`mailbox-icon-overlay ${isMailboxAnimating ? 'animating' : ''}`} onClick={transferMailsToInbox} title="Transfer all mails to Inbox">
                                <svg className="action-icon mailbox-sketch" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 12v7c0 .5.5 1 1 1h14c.5 0 1-.5 1-1v-7M4 12c0-4.4 3.6-8 8-8s8 3.6 8 8M4 12h16" />
                                  <path d="M12 4v16M8 8l2 2m6-2l-2 2" />
                                  <path d="M15 14H9v-1c0-.6.4-1 1-1h4c.6 0 1 .4 1 1v1z" />
                                </svg>
                                {hiddenMailCount > 0 && (
                                  <span className="hidden-mail-badge">{hiddenMailCount}</span>
                                )}
                              </span>
                            </div>
                            <div className="header-center">
                              <div
                                className="header-title-container"
                                onMouseEnter={() => setShowLogoutDropdown(true)}
                                onMouseLeave={() => setShowLogoutDropdown(false)}
                                onClick={() => setShowLogoutDropdown(!showLogoutDropdown)}
                              >
                                <h1 className="header-title">
                                  <span className="title-line">Letter</span>
                                  <span className="title-line">Box</span>
                                </h1>
                                {showLogoutDropdown && (
                                  <div className="logout-dropdown" ref={logoutDropdownRef}>
                                    <button className="logout-btn" onClick={handleLogout}>
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="logout-icon">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                        <polyline points="16 17 21 12 16 7" />
                                        <line x1="21" y1="12" x2="9" y2="12" />
                                      </svg>
                                      Logout
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="header-right">
                              <button
                                id="tutorial-compose-button"
                                className="compose-button icon-button"
                                onClick={startCompose}
                                title="Write Letter"
                              >
                                <svg className="action-icon write-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="currentColor" />
                                  <path d="M12 15L15 12" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                              <button id="tutorial-folder-dropdown-btn" className="folder-dropdown-btn" onClick={() => setShowFolderDropdown(!showFolderDropdown)} title="Folders">
                                <svg className="action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
                                </svg>
                              </button>
                            </div>
                          </header>
                          <div className="homepage-shortcuts">
                            <div className="shortcut-container">
                              <div className="shortcut-buttons">
                                <button
                                  className={`shortcut-btn to-send ${selectedFolderId === folders.find(f => f.name === 'Drafts')?.id ? 'active' : ''}`}
                                  onClick={() => {
                                    selectFolderByName('Drafts');
                                    setIsReceivedExpanded(false);
                                  }}
                                >
                                  <span className="shortcut-label">To Send</span>
                                </button>

                                <button
                                  className={`shortcut-btn received ${selectedFolderId === folders.find(f => f.name === 'Inbox')?.id ? 'active' : ''}`}
                                  onClick={() => {
                                    selectFolderByName('Inbox');
                                    setIsReceivedExpanded(!isReceivedExpanded);
                                  }}
                                >
                                  <span className="shortcut-label">Received</span>
                                </button>
                              </div>

                              <div className="search-container">
                                <input
                                  type="text"
                                  className="search-input"
                                  placeholder="Search letters..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(sanitizeInput(e.target.value))}
                                />
                                {globalSearchResults.length > 0 && (
                                  <div className="search-results-dropdown">
                                    {globalSearchResults.map(mail => (
                                      <div
                                        key={mail.id}
                                        className="search-result-item"
                                        onClick={() => {
                                          openMail(mail);
                                          setSearchTerm('');
                                        }}
                                      >
                                        <div className="result-main">
                                          <span className="result-subject">{mail.subject ? (mail.isHandwritten ? 'Handwritten Letter' : mail.subject) : '(No Subject)'}</span>
                                          <span className="result-folder">{mail.folderName}</span>
                                        </div>
                                        <div className="result-sub">
                                          {mail.type === 'sent' || mail.type === 'draft' ? `To: ${mail.recipient || 'Unknown'}` : `From: ${mail.sender || 'Unknown'}`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {isReceivedExpanded && (
                              <div className="expanded-folders">
                                <button
                                  className="shortcut-add-btn"
                                  onClick={() => {
                                    const name = window.prompt("Enter new folder name:");
                                    if (name && name.trim()) {
                                      handleCreateFolderWithName(name);
                                    }
                                  }}
                                  title="Create New Folder"
                                >
                                  +
                                </button>
                                <div className="sub-folders-list">
                                  {folders.filter(f => !['Bin', 'Hidden', 'Drafts', 'Inbox'].includes(f.name)).map(folder => (
                                    <button
                                      key={folder.id}
                                      className={`sub-folder-item ${selectedFolderId === folder.id ? 'selected' : ''}`}
                                      onClick={() => selectFolder(folder)}
                                      title={`${folder.name} (${folder.mailIds.length} letters)`}
                                    >
                                      <span className="sub-folder-icon">{getFolderIcon(folder.name)}</span>
                                      <span className="sub-folder-name">{folder.name}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="mailbox-container">
                            <div className={`mail-list ${selectedDeletedFolderId ? 'inside-folder' : ''}`}>
                              <div className="mailbox-header">
                                {(!selectedFolderId || !['Inbox', 'Drafts'].includes(folders.find(f => f.id === selectedFolderId)?.name) || selectedDeletedFolderId) && (
                                  <h2>
                                    {selectedDeletedFolderId ? (
                                      <span>
                                        <button className="back-btn" onClick={exitDeletedFolder}>Back to Bin</button>
                                        {deletedFolders.find(f => f.id === selectedDeletedFolderId)?.name || 'Unknown Folder'}
                                      </span>
                                    ) : (
                                      selectedFolderId ? (folders.find(f => f.id === selectedFolderId)?.name || 'Unknown Folder') : 'Loading Folders...'
                                    )}
                                  </h2>
                                )}
                                <div className="mailbox-actions">
                                  {folders.find(f => f.id === selectedFolderId)?.name === 'Drafts' && !selectedDeletedFolderId && (
                                    <button className="send-drafts-btn" onClick={sendAllDrafts}>Send All Letters</button>
                                  )}
                                  {folders.find(f => f.id === selectedFolderId)?.name === 'Bin' && !selectedDeletedFolderId && (
                                    <button className="empty-bin-btn" onClick={confirmEmptyBin}>Empty Bin</button>
                                  )}
                                </div>
                              </div>

                              {folders.find(f => f.id === selectedFolderId)?.name === 'Bin' && !selectedDeletedFolderId && deletedFolders.length > 0 && (
                                <div className="deleted-folders-section">
                                  <h3>Deleted Folders</h3>
                                  {deletedFolders.map(folder => (
                                    <div
                                      key={folder.id}
                                      className={`deleted-folder-item ${selectedDeletedFolderId === folder.id ? 'selected' : ''}`}
                                      onClick={() => selectDeletedFolder(folder.id)}
                                    >
                                      <span>{folder.name} ({folder.mailIds.length} mails)</span>
                                      <button
                                        className="restore-folder-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          confirmRestoreFolder(folder.id);
                                        }}
                                        title="Restore folder"
                                      >
                                        🔄 Restore
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="mail-grid">
                                {filteredMails.map(mail => (
                                  <div
                                    key={mail.id}
                                    className="mail-card"
                                    onClick={() => openMail(mail)}
                                  >
                                    <div className="mail-item">
                                      <div
                                        className="mail-preview-content"
                                        style={{
                                          backgroundColor: mail.backgroundColor || undefined,
                                          color: mail.textColorVar || undefined,
                                          '--custom-bg': mail.backgroundColor || undefined
                                        }}
                                      >
                                        {mail.isHandwritten ? (
                                          <img src={mail.content} alt="Content Preview" />
                                        ) : (
                                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mail.content) }}></div>
                                        )}
                                      </div>
                                      {mail.folder === 'Drafts' && (
                                        <button
                                          className="delete-mail-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            confirmDeleteMail(mail.id);
                                          }}
                                          title="Delete draft"
                                        >
                                          🗑️
                                        </button>
                                      )}
                                      {mail.folder === 'Bin' && (
                                        <button
                                          className="restore-mail-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            confirmRestoreMail(mail.id);
                                          }}
                                          title="Restore to Inbox"
                                        >
                                          🔄
                                        </button>
                                      )}
                                      {mail.type !== 'draft' && mail.stamp && (
                                        <span className="mail-item-stamp">{mail.stamp}</span>
                                      )}
                                    </div>
                                    <div className="mail-info">
                                      <div className="mail-header">
                                        <strong>{mail.subject ? (mail.isHandwritten ? 'Handwritten Letter' : mail.subject) : '(No Subject)'}</strong>
                                        <span className="mail-date">
                                          {mail.created_at ? new Date(mail.created_at).toLocaleDateString() : ''}
                                        </span>
                                      </div>
                                      <div className="mail-sender">
                                        {selectedFolderId && (folders.find(f => f.id === selectedFolderId)?.name === 'Sent' || folders.find(f => f.id === selectedFolderId)?.name === 'Drafts') ?
                                          `To: ${mail.recipient || 'Unknown'}` :
                                          `From: ${mail.sender_username || mail.sender_email || 'Unknown'}`
                                        }
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="taskbar">
                              <div className="taskbar-bin">
                                {folders.filter(f => f.name === 'Bin').map(folder => (
                                  <button
                                    key={folder.id}
                                    className={`taskbar-bin-btn ${selectedFolderId === folder.id ? 'selected' : ''}`}
                                    onClick={() => selectFolder(folder)}
                                    title={`Bin (${folder.mailIds.length} items)`}
                                  >
                                    🗑️
                                  </button>
                                ))}
                              </div>
                            </div>
                            {selectedMail && (
                              <div className="mail-popup-overlay" onClick={closeMail}>
                                <div className="mail-popup-content" onClick={(e) => e.stopPropagation()}>
                                  {(() => {
                                    const mailFolder = folders.find(f => f.mailIds.includes(selectedMail.id));
                                    return (
                                      <React.Fragment key={selectedMail.id}>
                                        <div className={`mail-detail ${mailFolder && mailFolder.name === 'Bin' ? 'bin-mail' : ''} ${selectedMail.type === 'draft' ? 'draft-mail' : ''} ${selectedMail.type === 'sent' ? 'sent-mail' : ''}`}>
                                          <button className="close-popup-btn" onClick={closeMail}>✕</button>

                                          {selectedMail.type !== 'draft' && (
                                            selectedMail.isHandwritten ? (
                                              <img src={selectedMail.subject} alt="Subject" className="handwritten-subject" />
                                            ) : (
                                              <h3>{selectedMail.subject}</h3>
                                            )
                                          )}
                                          <p><strong>{selectedMail.type === 'sent' ? 'To:' : 'From:'}</strong> {selectedMail.type === 'sent' ? (selectedMail.recipient || 'Unknown') : (selectedMail.sender || 'Unknown')}</p>
                                          <p style={selectedMail.type === 'draft' ? { display: 'flex', alignItems: 'center', gap: '8px' } : {}}>
                                            <strong>{selectedMail.type === 'draft' ? 'Subject:' : 'Date:'}</strong>{' '}
                                            {selectedMail.type === 'draft' ? (
                                              selectedMail.isHandwritten ? (
                                                <img src={selectedMail.subject} alt="Subject" className="handwritten-subject" style={{ maxHeight: '30px', margin: 0, display: 'inline-block' }} />
                                              ) : (
                                                selectedMail.subject
                                              )
                                            ) : (
                                              selectedMail.date
                                            )}
                                          </p>
                                          <div ref={mailBodyRef} className="mail-body" style={{ background: selectedMail.backgroundColor || undefined, '--custom-bg': selectedMail.backgroundColor || undefined, color: selectedMail.textColorVar || undefined }}>
                                            {selectedMail.isHandwritten ? (
                                              <img src={selectedMail.content} alt="Handwritten Content" className="handwritten-content" />
                                            ) : (
                                              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(adjustImagePositionsForDisplay(selectedMail.content, selectedMail.type)) }}></div>
                                            )}
                                          </div>
                                          {selectedMail.type !== 'draft' && selectedMail.stamp && (
                                            <div className="letter-stamp" title="Letter Stamp">
                                              {selectedMail.stamp}
                                            </div>
                                          )}
                                        </div>
                                        <div className="mail-footer-actions">
                                          {selectedMail.sender !== 'You' && selectedMail.type !== 'draft' && (
                                            <select className="move-to-folder" onChange={(e) => {
                                              if (e.target.value) {
                                                moveMailToFolder(selectedMail.id, e.target.value);
                                                e.target.value = '';
                                              }
                                            }}>
                                              <option value="">Move to folder</option>
                                              {folders.filter(f => !f.mailIds.includes(selectedMail.id) && !['Sent', 'Bin', 'Drafts', 'Hidden'].includes(f.name)).map(folder => (
                                                <option key={folder.id} value={folder.id}>{folder.name}</option>
                                              ))}
                                            </select>
                                          )}
                                          {selectedMail.sender !== 'You' && selectedMail.type !== 'draft' && (
                                            <button className="reply-button" onClick={() => startReply(selectedMail)}>Reply</button>
                                          )}
                                          {selectedMail.type === 'draft' && (
                                            <button className="edit-draft-button" onClick={() => editDraft(selectedMail)}>Edit Draft</button>
                                          )}
                                          {(() => {
                                            if (mailFolder && mailFolder.name !== 'Bin' && selectedMail.type !== 'sent') {
                                              return (
                                                <button className="delete-mail-btn" onClick={() => { confirmDeleteMail(selectedMail.id); }}>Delete</button>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </div>
                                      </React.Fragment>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                            {isComposing && (
                              <div className="compose-popup-overlay" onClick={handleCloseCompose}>
                                <div className="compose-popup-content" onClick={(e) => e.stopPropagation()}>
                                  <button className="close-popup-btn" onClick={handleCloseCompose}>✕</button>
                                  <div className="compose-mode-selector">
                                    <button type="button" className={`mode-tab ${composeMode === 'type' ? 'active' : ''}`} onClick={() => handleModeToggle('type')}>Type</button>
                                    <button type="button" className={`mode-tab ${composeMode === 'draw' ? 'active' : ''}`} onClick={() => handleModeToggle('draw')}>Draw</button>
                                  </div>
                                  <div className="compose-form">
                                    <h3>{composeMode === 'draw' ? 'Draw a Letter' : 'Write a Letter'}</h3>
                                    <div className="form-group">
                                      <label>To:</label>
                                      <input
                                        type="text"
                                        name="to"
                                        value={composeForm.to}
                                        onChange={handleComposeChange}
                                        placeholder="Recipient username, or email"
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>Subject:</label>
                                      {composeMode === 'draw' ? (
                                        <DrawingCanvas
                                          width={600}
                                          height={60}
                                          placeholder="Draw subject here..."
                                          value={composeForm.subject}
                                          onChange={(val) => setComposeForm(prev => ({ ...prev, subject: val }))}
                                          brushColor={composeForm.textColorVar || '#34495e'}
                                          brushSize={brushSize}
                                          isEraser={isEraser}
                                          backgroundColor={composeForm.backgroundColor}
                                          brushType={brushType}
                                          onDrawingStateChange={(isDrawing) => { isUserDrawing.current = isDrawing; }}
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          name="subject"
                                          value={composeForm.subject}
                                          onChange={handleComposeChange}
                                          placeholder="Letter subject"
                                        />
                                      )}
                                    </div>
                                    <div className="form-group message-group">
                                      <label>Message:</label>
                                      {composeMode === 'draw' ? (
                                        <div className="drawing-workspace">
                                          <div className="drawing-toolbar">
                                            <div className="tool-group">
                                              <button type="button" className={`tool-btn ${!isEraser ? 'active' : ''}`} onClick={() => setIsEraser(false)}>🖌️ Brush</button>
                                              <button type="button" className={`tool-btn ${isEraser ? 'active' : ''}`} onClick={() => setIsEraser(true)}>🧽 Eraser</button>
                                            </div>
                                            <div className="tool-group">
                                              <button type="button" className={`size-btn ${brushSize === 1 ? 'active' : ''}`} onClick={() => setBrushSize(1)}>Small</button>
                                              <button type="button" className={`size-btn ${brushSize === 4 ? 'active' : ''}`} onClick={() => setBrushSize(4)}>Medium</button>
                                              <button type="button" className={`size-btn ${brushSize === 8 ? 'active' : ''}`} onClick={() => setBrushSize(8)}>Large</button>
                                            </div>
                                            <div className="tool-group">
                                              <button type="button" className={`type-btn ${brushType === 'normal' ? 'active' : ''}`} onClick={() => setBrushType('normal')} title="Normal Brush" style={{ fontSize: '1.2em' }}>🖌️</button>
                                              <button type="button" className={`type-btn ${brushType === 'calligraphy' ? 'active' : ''}`} onClick={() => setBrushType('calligraphy')} title="Calligraphy" style={{ fontSize: '1.2em' }}>🖋️</button>
                                              <button type="button" className={`type-btn ${brushType === 'soft' ? 'active' : ''}`} onClick={() => setBrushType('soft')} title="Soft Brush" style={{ fontSize: '1.2em' }}>🌫️</button>
                                              <button type="button" className="tool-btn" onClick={() => setDrawingHeight(prev => prev + 200)} title="Extend Length">Extend ↕️</button>
                                            </div>
                                            <div className="tool-group color-pickers">
                                              <div ref={textColorPickerRef} style={{ position: 'relative', display: 'inline-block' }}>
                                                <button ref={textColorButtonRef} type="button" onClick={() => setShowTextColorPicker(!showTextColorPicker)} style={{ backgroundColor: buttonColor, width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer' }} title="Brush Color"></button>
                                                {showTextColorPicker && (
                                                  <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000 }}>
                                                    <SketchPicker color={selectedColor} onChange={(color) => { setSelectedColor(color.hex); setButtonColor(color.hex); handleTextColorChange(color.hex); }} />
                                                  </div>
                                                )}
                                              </div>
                                              <div ref={wholeLetterBackgroundColorPickerRef} style={{ position: 'relative', display: 'inline-block' }}>
                                                <button ref={wholeLetterBackgroundColorButtonRef} type="button" onClick={() => setShowWholeLetterBackgroundColorPicker(!showWholeLetterBackgroundColorPicker)} style={{ backgroundColor: buttonWholeLetterBackgroundColor, width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer' }} title="Background Color"></button>
                                                {showWholeLetterBackgroundColorPicker && (
                                                  <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000 }}>
                                                    <SketchPicker color={selectedWholeLetterBackgroundColor} onChange={(color) => { setSelectedWholeLetterBackgroundColor(color.hex); setButtonWholeLetterBackgroundColor(color.hex); }} onChangeComplete={(color) => handleWholeLetterBackgroundColorChange(color.hex)} />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <DrawingCanvas
                                            width={600}
                                            height={drawingHeight}
                                            placeholder="Draw your message here..."
                                            value={composeForm.content}
                                            onChange={(val) => setComposeForm(prev => ({ ...prev, content: val }))}
                                            brushColor={composeForm.textColorVar || '#34495e'}
                                            brushSize={brushSize}
                                            isEraser={isEraser}
                                            backgroundColor={composeForm.backgroundColor}
                                            brushType={brushType}
                                            onDrawingStateChange={(isDrawing) => { isUserDrawing.current = isDrawing; }}
                                          />
                                        </div>
                                      ) : (
                                        <>
                                          <div className="formatting-toolbar">
                                            <select value={editorFormatting.font} onChange={handleFontChange} title="Font Type">
                                              <option value="Ibarra Real Nova">Ibarra Real Nova</option>
                                              <option value="Arial">Arial</option>
                                              <option value="Times New Roman">Times New Roman</option>
                                              <option value="Courier New">Courier New</option>
                                              <option value="Georgia">Georgia</option>
                                              <option value="Verdana">Verdana</option>
                                              <option value="Helvetica">Helvetica</option>
                                              <option value="Comic Sans MS">Comic Sans MS</option>
                                              <option value="Impact">Impact</option>
                                              <option value="Tahoma">Tahoma</option>
                                              <option value="Trebuchet MS">Trebuchet MS</option>
                                            </select>
                                            <select value={editorFormatting.fontSize} onChange={handleFontSizeChange} title="Font Size">
                                              <option value="">Size</option>
                                              <option value="1">10px</option>
                                              <option value="2">13px</option>
                                              <option value="3">16px</option>
                                              <option value="4">18px</option>
                                              <option value="5">24px</option>
                                              <option value="6">32px</option>
                                              <option value="7">48px</option>
                                            </select>
                                            <button type="button" className={editorFormatting.bold ? 'active' : ''} onClick={() => execCommand('bold')} title="Bold">B</button>
                                            <button type="button" className={editorFormatting.italic ? 'active' : ''} onClick={() => execCommand('italic')} title="Italic">I</button>
                                            <button type="button" className={editorFormatting.underline ? 'active' : ''} onClick={() => execCommand('underline')} title="Underline">U</button>
                                            <div ref={textColorPickerRef} style={{ position: 'relative', display: 'inline-block' }}>
                                              <button ref={textColorButtonRef} type="button" onClick={() => setShowTextColorPicker(!showTextColorPicker)} style={{ backgroundColor: buttonColor, width: '20px', height: '20px', border: '1px solid #ccc', cursor: 'pointer' }} title="Text Color (Selection)"></button>
                                              {showTextColorPicker && (
                                                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000 }}>
                                                  <SketchPicker color={selectedColor} onChange={(color) => { setSelectedColor(color.hex); setButtonColor(color.hex); handleTextColorChange(color.hex); }} />
                                                </div>
                                              )}
                                            </div>
                                            <div ref={highlightColorPickerRef} style={{ position: 'relative', display: 'inline-block' }}>
                                              <button ref={highlightColorButtonRef} type="button" onClick={() => setShowHighlightColorPicker(!showHighlightColorPicker)} style={{ backgroundColor: buttonHighlightColor, width: '20px', height: '20px', border: '1px solid #ccc', cursor: 'pointer' }} title="Highlight Color (Selection)"></button>
                                              {showHighlightColorPicker && (
                                                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000 }}>
                                                  <SketchPicker color={selectedHighlightColor} onChange={(color) => { setSelectedHighlightColor(color.hex); setButtonHighlightColor(color.hex); handleHighlightColorChange(color.hex); }} />
                                                </div>
                                              )}
                                            </div>
                                            <div ref={wholeLetterBackgroundColorPickerRef} style={{ position: 'relative', display: 'inline-block' }}>
                                              <button ref={wholeLetterBackgroundColorButtonRef} type="button" onClick={() => setShowWholeLetterBackgroundColorPicker(!showWholeLetterBackgroundColorPicker)} style={{ backgroundColor: buttonWholeLetterBackgroundColor, width: '20px', height: '20px', border: '1px solid #ccc', cursor: 'pointer' }} title="Whole Letter Background Color"></button>
                                              {showWholeLetterBackgroundColorPicker && (
                                                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000 }}>
                                                  <SketchPicker color={selectedWholeLetterBackgroundColor} onChange={(color) => { setSelectedWholeLetterBackgroundColor(color.hex); setButtonWholeLetterBackgroundColor(color.hex); }} onChangeComplete={(color) => handleWholeLetterBackgroundColorChange(color.hex)} />
                                                </div>
                                              )}
                                            </div>
                                            <button type="button" className={editorFormatting.alignLeft ? 'active' : ''} onClick={() => execCommand('justifyLeft')} title="Align Left">L</button>
                                            <button type="button" className={editorFormatting.alignCenter ? 'active' : ''} onClick={() => execCommand('justifyCenter')} title="Align Center">C</button>
                                            <button type="button" className={editorFormatting.alignRight ? 'active' : ''} onClick={() => execCommand('justifyRight')} title="Align Right">R</button>
                                            <button type="button" className={editorFormatting.alignJustify ? 'active' : ''} onClick={() => execCommand('justifyFull')} title="Justify">J</button>
                                            <button type="button" onClick={() => insertList('ordered')} title="Numbered List">1.</button>
                                            <button type="button" onClick={() => insertList('unordered')} title="Bulleted List">•</button>
                                            <select value={editorFormatting.lineHeight} onChange={handleLineSpacingChange} title="Line Spacing">
                                              <option value="1">1.0</option>
                                              <option value="1.15">1.15</option>
                                              <option value="1.5">1.5</option>
                                              <option value="2">2.0</option>
                                            </select>
                                            <button type="button" onClick={handleAttachPhoto} title="Attach Photo">📎</button>
                                            <button type="button" onClick={undo} title="Undo">↶</button>
                                            <button type="button" onClick={redo} title="Redo">↷</button>
                                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
                                          </div>
                                          <div
                                            ref={contentRef}
                                            contentEditable
                                            className="content-editor"
                                            placeholder="Write your letter here..."
                                            spellCheck="false"
                                            onInput={handleContentChange}
                                            onKeyDown={handleKeyDown}
                                            onMouseDown={(e) => {
                                              const wrapper = e.target.closest('.image-wrapper');
                                              if (wrapper) {
                                                handleImageMouseDown(e);
                                              } else {
                                                setSelectedImageWrapper(null);
                                              }
                                            }}
                                            style={{ background: composeForm.backgroundColor || undefined, '--custom-bg': composeForm.backgroundColor || undefined, color: composeForm.textColorVar || undefined }}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="form-actions">
                                    <button className="send-button" onClick={saveAsDraft}>
                                      {isSaving ? 'Saving...' : editingDraftId ? 'Update Draft' : 'Save as Draft'}
                                    </button>
                                    <button className="cancel-button" onClick={cancelCompose}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {showConfirmEmptyBin && (
                              <div className="confirm-popup">
                                <div className="confirm-popup-content">
                                  <h3>Empty Bin</h3>
                                  <p>Are you sure you want to permanently delete all items in the bin? This action cannot be undone.</p>
                                  <div className="confirm-popup-actions">
                                    <button className="confirm-btn" onClick={emptyBin}>Empty Bin</button>
                                    <button className="cancel-btn" onClick={cancelEmptyBin}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {showConfirmRestore && (
                              <div className="confirm-popup">
                                <div className="confirm-popup-content">
                                  <h3>Restore Folder</h3>
                                  <p>Are you sure you want to restore this folder? All mails in this folder will be restored from the bin.</p>
                                  <div className="confirm-popup-actions">
                                    <button className="restore-folder-btn" onClick={restoreFolder}>Restore</button>
                                    <button className="cancel-btn" onClick={cancelRestoreFolder}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {showConfirmDelete && (
                              <div className="confirm-popup">
                                <div className="confirm-popup-content">
                                  <h3>Delete Mail</h3>
                                  <p>Are you sure you want to delete this mail? It will be moved to the Bin.</p>
                                  <div className="confirm-popup-actions">
                                    <button className="confirm-btn" onClick={() => { deleteMail(selectedMail.id); closeMail(); setShowConfirmDelete(false); }}>Delete</button>
                                    <button className="cancel-btn" onClick={cancelDeleteMail}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {showConfirmRestoreMail && (
                              <div className="confirm-popup">
                                <div className="confirm-popup-content">
                                  <h3>Restore Mail</h3>
                                  <p>Are you sure you want to restore this mail? It will be moved back to its original folder.</p>
                                  <div className="confirm-popup-actions">
                                    <button className="restore-folder-btn" onClick={() => { restoreMail(mailToRestore); closeMail(); setShowConfirmRestoreMail(false); setMailToRestore(null); }}>Restore</button>
                                    <button className="cancel-btn" onClick={cancelRestoreMail}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {showConfirmDeleteFolder && (
                              <div className="confirm-popup">
                                <div className="confirm-popup-content">
                                  <h3>Delete Folder</h3>
                                  <p>Are you sure you want to delete this folder? All mails in this folder will be moved to the Bin.</p>
                                  <div className="confirm-popup-actions">
                                    <button className="confirm-btn" onClick={confirmDeleteFolder}>Delete</button>
                                    <button className="cancel-btn" onClick={cancelDeleteFolder}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {showConfirmDeleteImage && (
                              <div className="confirm-popup">
                                <div className="confirm-popup-content">
                                  <h3>Delete Photo</h3>
                                  <p>Are you sure you want to delete this attached photo? This action cannot be undone.</p>
                                  <div className="confirm-popup-actions">
                                    <button className="confirm-btn" onClick={confirmDeleteImage}>Delete</button>
                                    <button className="cancel-btn" onClick={cancelDeleteImage}>Cancel</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {expandedImage && (
                              <div className="image-popup-overlay" onClick={closeExpandedImage}>
                                <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
                                  <button className="image-popup-close-btn" onClick={closeExpandedImage}>✕</button>
                                  <div className="expanded-image-wrapper">
                                    <img src={expandedImage} alt="Expanded" className="expanded-image" />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div >
                          {showTutorial && (
                            <div className={`tutorial-overlay ${spotlightRect ? 'has-spotlight' : ''}`} style={spotlightRect ? {
                              '--spotlight-top': `${spotlightRect.top - spotlightRect.padding.top}px`,
                              '--spotlight-left': `${spotlightRect.left - spotlightRect.padding.left}px`,
                              '--spotlight-width': `${spotlightRect.width + spotlightRect.padding.left + spotlightRect.padding.right}px`,
                              '--spotlight-height': `${spotlightRect.height + spotlightRect.padding.top + spotlightRect.padding.bottom}px`
                            } : {}}>
                              <div className={`tutorial-card ${spotlightRect ? 'contextual' : 'centered'}`} style={(() => {
                                if (!spotlightRect) return {};
                                const placement = tutorialSteps[tutorialStep].placement || 'bottom';
                                const style = { position: 'absolute', width: '300px', margin: 0 };

                                if (placement === 'right') {
                                  // Move even higher (-200) for better visibility next to folders
                                  style.top = `${Math.max(20, Math.min(window.innerHeight - 300, spotlightRect.top + spotlightRect.height / 2 - 200))}px`;
                                  style.left = `${spotlightRect.left + spotlightRect.width + 30}px`;
                                  style.bottom = 'auto';
                                } else if (placement === 'top') {
                                  // Calculate highlight boundaries
                                  const highlightTop = spotlightRect.top - spotlightRect.padding.top;

                                  style.bottom = `${window.innerHeight - highlightTop + 20}px`;
                                  style.top = 'auto';
                                  // Strictly constrain height to available space at the top
                                  style.maxHeight = `${Math.max(100, highlightTop - 40)}px`;

                                  // Centering with safe margins
                                  const cardWidth = window.innerWidth > 768 ? 320 : 280;
                                  const leftPos = spotlightRect.left + spotlightRect.width / 2 - (cardWidth / 2);
                                  style.left = Math.max(15, Math.min(window.innerWidth - (cardWidth + 15), leftPos)) + 'px';
                                  style.width = `${cardWidth}px`;
                                } else {
                                  // Default bottom placement
                                  const highlightBottom = spotlightRect.top + spotlightRect.height + spotlightRect.padding.bottom;
                                  const availableBottom = window.innerHeight - highlightBottom;

                                  style.top = `${highlightBottom + 20}px`;
                                  style.bottom = 'auto';
                                  // Strictly constrain height to available space at the bottom
                                  style.maxHeight = `${Math.max(100, availableBottom - 40)}px`;

                                  const cardWidth = window.innerWidth > 768 ? 320 : 280;
                                  const leftPos = spotlightRect.left + spotlightRect.width / 2 - (cardWidth / 2);
                                  style.left = Math.max(15, Math.min(window.innerWidth - (cardWidth + 15), leftPos)) + 'px';
                                  style.width = `${cardWidth}px`;
                                }
                                return style;
                              })()}>
                                <div className="tutorial-card-content" key={tutorialStep}>
                                  <div className="tutorial-header">
                                    <div className="tutorial-icon-wrapper">
                                      <span className="tutorial-icon">{tutorialSteps[tutorialStep].icon}</span>
                                    </div>
                                    <div className="tutorial-title-area">
                                      <h2>{tutorialSteps[tutorialStep].title}</h2>
                                      <p className="tutorial-tagline">{tutorialSteps[tutorialStep].description}</p>
                                    </div>
                                  </div>
                                  <div className="tutorial-body">
                                    <p>{tutorialSteps[tutorialStep].content}</p>
                                  </div>
                                  <div className="tutorial-footer">
                                    <div className="tutorial-progress">
                                      {tutorialSteps.map((_, index) => (
                                        <div
                                          key={index}
                                          className={`progress-dot ${index === tutorialStep ? 'active' : ''}`}
                                        ></div>
                                      ))}
                                    </div>
                                    <div className="tutorial-actions">
                                      {tutorialStep > 0 && (
                                        <button className="tutorial-back-btn" onClick={handlePrevStep}>Back</button>
                                      )}
                                      <button className="tutorial-next-btn" onClick={handleNextStep}>
                                        {tutorialStep === tutorialSteps.length - 1 ? "Get Started" : "Next"}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div >
                      </>
        }
        />
      </Routes>
    </Router>
  );
}

export default App;
